// <sale-page-flow> — the Labor Day 2026 clearance flow, forked from
// clearance-v3.js so sections/featured-products-page.liquid can diverge without touching
// sections/clearance-v3.liquid, which keeps using clearance-v3.js unchanged.
//
// Trimmed relative to the v3 original: the Quick Bundles pack carousel and the
// Choose style tab (tiles, drill-down rows, derived 3-pack bundles) are gone, so
// Last Call Clearance is the only view. The data-clearance-v3-* attribute names and
// clearance-v3.css are still shared with v3 on purpose.

// Mode check that tolerates the shared resolver in snippets/global-js.liquid not
// having run yet. Quantity mode is the safe default here: it is what the section
// rendered before tag mode existed, and it never depends on per-card tag data.
function isClearanceTagModeSafe() {
  return typeof isClearanceTagMode === 'function' ? isClearanceTagMode() : false
}

// Resolves once the page's own resources (images, scripts) have loaded.
function waitForPageLoad() {
  if (document.readyState === 'complete') return Promise.resolve()
  return new Promise(function resolveOnLoad(resolve) {
    window.addEventListener('load', resolve, { once: true })
  })
}

// Resolves when the browser has a spare moment (Safari has no requestIdleCallback).
function waitForIdle() {
  return new Promise(function resolveWhenIdle(resolve) {
    if ('requestIdleCallback' in window) requestIdleCallback(resolve, { timeout: 3000 })
    else setTimeout(resolve, 300)
  })
}

// Footnote copy keyed by the Pacific weekday. Any other day keeps the section setting.
const CLEARANCE_FOOTNOTE_BY_WEEKDAY = {
  Sunday: 'ENDS TOMORROW',
  Monday: 'ENDS TONIGHT',
}

// The timer stays hidden until the last 48 hours, so its first visible reading is
// 47:59:59 — 2 days out to the second, which is the widest it ever gets.
const COUNTDOWN_VISIBLE_FROM_SECONDS = 48 * 60 * 60 - 1

const pacificWeekdayFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  weekday: 'long',
})

function getPacificWeekday() {
  return pacificWeekdayFormatter.format(new Date())
}

function buildCountdownDigitCell(digit) {
  const cell = document.createElement('span')
  cell.className = 'hero-countdown__digit'
  cell.textContent = digit
  return cell
}

if (!customElements.get('sale-page-flow')) {
  // Sitewide sizing profile. A section can scope itself to its own key (the women's
  // Labor Day page does) so its picks never overwrite this one.
  const DEFAULT_SIZE_STORAGE_KEY = 'tc_sizes'

  // Matches the design's ±320px arrow step for both carousel rows.
  const CAROUSEL_SCROLL_STEP = 320

  // Same breakpoint the stylesheets use for the mobile card sizing.
  const CARD_SCROLL_MEDIA_QUERY = '(max-width: 760px)'

  // Space left between the sticky pills and the first row when a pill tap scrolls back up.
  const PILL_ROWS_SCROLL_GAP = 24

  class SalePageFlow extends HTMLElement {
    constructor() {
      super()
      this.selectedTopSize = ''
      this.selectedBottomWaist = ''
      this.selectedBottomLength = ''
      // Letter-sized bottoms (XSM–4XLG), separate from waist/length.
      this.selectedBottomSize = ''
      // Left unset so no Tops Length button starts preselected; filtering treats an
      // unset value as 'Regular' via getEffectiveTopLength().
      this.selectedTopLength = ''
      this.activeTab = 'deals'
      this.singlesAbortController = null
      // Last Call Clearance is server-paginated: the section renders one `paginate` page
      // per request, and Load More fetches the next one. Both counts come from the grid's
      // data attributes, so the page size lives in Liquid alone.
      this.singlesNextPage = 0
      this.isLoadingMoreSingles = false
      this.hasMoreSingles = false
      this.visibleSingleCount = 0
      this.clearanceCount = 0
      this.clearanceSubtotal = 0
      this.cartVariantIds = new Set()
      this.cartVariantQuantities = new Map()
      this.tiers = []
      this.baseDiscount = 0
      this.handleSizeClick = this.handleSizeClick.bind(this)
      this.handleWaistClick = this.handleWaistClick.bind(this)
      this.handleLengthClick = this.handleLengthClick.bind(this)
      this.handleTopLengthClick = this.handleTopLengthClick.bind(this)
      this.handleBottomSizeClick = this.handleBottomSizeClick.bind(this)
      this.handleShowDeals = this.handleShowDeals.bind(this)
      // A/B test concluded: this.handleShopAllBundles = this.handleShopAllBundles.bind(this)
      this.handleChangeSize = this.handleChangeSize.bind(this)
      this.handleCarouselClick = this.handleCarouselClick.bind(this)
      this.handleDealQuickViewPrefetch = this.prefetchDealQuickView.bind(this)
      this.handleCustomPackOnlyContent = this.showCustomPackOnlyContent.bind(this)
      this.handleSingleAdd = this.handleSingleAdd.bind(this)
      this.handleCartRefresh = this.refreshCartSummary.bind(this)
      this.handleQuickAddSuccess = this.handleQuickAddSuccess.bind(this)
      this.handleModalSizeClick = this.handleModalSizeClick.bind(this)
      this.handleModalWaistClick = this.handleModalWaistClick.bind(this)
      this.handleModalLengthClick = this.handleModalLengthClick.bind(this)
      this.handleModalTopLengthClick = this.handleModalTopLengthClick.bind(this)
      this.handleModalApply = this.applyModalSize.bind(this)
      this.handleCloseModal = this.closeSizeModal.bind(this)
      this.handleLoadMoreSingles = this.loadMoreSingles.bind(this)
      this.handleHeaderOffsetResize = this.syncHeaderOffset.bind(this)
      this.handleCountdownTick = this.tickHeroCountdown.bind(this)
      this.handleCarouselArrowsResize = this.syncCarouselArrows.bind(this)
    }

    connectedCallback() {
      this.hero = this.querySelector('#hero')
      this.productsArea = this.querySelector('#productsArea')
      this.browseHeader = this.querySelector('.browse-header')
      this.stepOne = this.querySelector('[data-clearance-v3-step-one]')
      this.stepTwo = this.querySelector('[data-clearance-v3-step-two]')
      this.sizeCard = this.querySelector('[data-clearance-v3-size-card]')
      this.sizeButtons = Array.from(this.querySelectorAll('[data-clearance-v3-size-option]'))
      this.showDealsButton = this.querySelector('[data-clearance-v3-show-deals]')
      this.changeSizeButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-change-size]')
      )
      this.selectedSizeLabel = this.querySelector('[data-clearance-v3-selected-size-label]')
      this.dealsView = this.querySelector('[data-clearance-v3-deals-view]')
      this.dealMediaCarousels = Array.from(
        this.querySelectorAll('[data-sale-page-media-carousel]')
      )
      this.dealMediaSwipers = new Map()
      /* A/B test concluded: automatic bundles DOM references retained for reference.
      this.automaticBundlesGrid = this.querySelector(
        '[data-clearance-v3-automatic-bundles-grid]'
      )
      this.automaticBundlesLoading = this.querySelector(
        '[data-clearance-v3-automatic-bundles-loading]'
      )
      this.automaticBundlesEmpty = this.querySelector(
        '[data-clearance-v3-automatic-bundles-empty]'
      )
      this.automaticBundlesShopAll = this.querySelector(
        '[data-clearance-v3-automatic-bundles-shop-all]'
      )
      */
      this.singleCards = Array.from(this.querySelectorAll('[data-clearance-v3-single-card]'))
      for (const card of this.singleCards) this.stripLengthIfNotEligibleForLengthFilter(card)
      this.addSingleButtons = Array.from(this.querySelectorAll('[data-clearance-v3-add-single]'))
      this.singlesCountElement = this.querySelector('[data-clearance-v3-singles-count]')
      this.singlesGrid = this.querySelector('[data-clearance-v3-singles-grid]')
      this.singlesSentinel = this.querySelector('[data-clearance-v3-singles-sentinel]')
      this.loadMoreButton = this.querySelector('[data-clearance-v3-load-more]')
      // Written by Liquid's `paginate`: the page Load More should ask for next. Empty means
      // this is the last page.
      this.singlesNextPage = Number(this.singlesGrid?.dataset.nextPage || 0)
      this.emptyElement = this.querySelector('[data-clearance-v3-empty]')
      this.sizeModal = this.querySelector('[data-clearance-v3-size-modal]')
      this.sizeModalOverlay = this.querySelector('[data-clearance-v3-modal-overlay]')
      this.modalSizeButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-size-option]')
      )
      this.modalWaistButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-waist-option]')
      )
      this.modalLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-length-option]')
      )
      this.modalTopLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-top-length-option]')
      )
      this.modalApplyButton = this.querySelector('[data-clearance-v3-modal-apply]')
      this.modalCloseButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-modal-close]')
      )
      this.modalBottomsSection = this.querySelector('.sz-modal-bottoms-size')
      this.toastWrap = this.querySelector('[data-clearance-v3-toast-wrap]')
      this.bottomCard = this.querySelector('[data-clearance-v3-bottom-card]')
      this.bottomSizePrompt = this.querySelector('[data-sale-page-bottom-size-prompt]')
      this.waistButtons = Array.from(this.querySelectorAll('[data-clearance-v3-waist-option]'))
      this.lengthButtons = Array.from(this.querySelectorAll('[data-clearance-v3-length-option]'))
      this.topLengthButtons = Array.from(
        this.querySelectorAll('[data-clearance-v3-top-length-option]')
      )
      // Step 1 and modal Bottoms rows share one handler and stay in lockstep.
      this.bottomSizeButtons = Array.from(
        this.querySelectorAll('[data-tc-bottom-size-option], [data-tc-modal-bottom-size-option]')
      )
      this.tiers = this.parseTiers()
      this.baseDiscount = this.parseBaseDiscount()
      this.clearanceVariantIds = this.buildClearanceVariantIds()

      for (const button of this.sizeButtons) {
        button.addEventListener('click', this.handleSizeClick)
      }

      for (const button of this.waistButtons) {
        button.addEventListener('click', this.handleWaistClick)
      }

      for (const button of this.lengthButtons) {
        button.addEventListener('click', this.handleLengthClick)
      }

      for (const button of this.topLengthButtons) {
        button.addEventListener('click', this.handleTopLengthClick)
      }

      for (const button of this.bottomSizeButtons) {
        button.addEventListener('click', this.handleBottomSizeClick)
      }

      for (const button of this.addSingleButtons) {
        button.addEventListener('click', this.handleSingleAdd)
      }

      for (const button of this.modalSizeButtons) {
        button.addEventListener('click', this.handleModalSizeClick)
      }

      for (const button of this.modalWaistButtons) {
        button.addEventListener('click', this.handleModalWaistClick)
      }

      for (const button of this.modalLengthButtons) {
        button.addEventListener('click', this.handleModalLengthClick)
      }

      for (const button of this.modalTopLengthButtons) {
        button.addEventListener('click', this.handleModalTopLengthClick)
      }

      for (const button of this.modalCloseButtons) {
        button.addEventListener('click', this.handleCloseModal)
      }

      this.modalApplyButton?.addEventListener('click', this.handleModalApply)
      this.sizeModalOverlay?.addEventListener('click', this.handleCloseModal)
      this.showDealsButton?.addEventListener('click', this.handleShowDeals)
      this.addEventListener('click', this.handleCarouselClick)
      for (const type of ['pointerover', 'pointerdown', 'focusin'])
        this.addEventListener(type, this.handleDealQuickViewPrefetch)
      for (const button of this.changeSizeButtons) {
        button.addEventListener('click', this.handleChangeSize)
      }
      this.setupDealMediaCarousels()
      // A/B test concluded: automatic bundles Shop all listener disabled.
      document.addEventListener('cartaddsuccess', this.handleCartRefresh)
      document.addEventListener('cartupdatesuccess', this.handleCartRefresh)
      document.addEventListener('clearanceatcsuccess', this.handleQuickAddSuccess)

      this.loadMoreButton?.addEventListener('click', this.handleLoadMoreSingles)
      this.setupFireRimPausing()
      this.setupSinglesAutoLoad()
      this.setupGridToggle()
      this.preloadCustomPackOnlyQuickAdds()

      this.restoreSelectedSize()
      this.updateTierStrip({ count: this.clearanceCount, subtotal: this.clearanceSubtotal })
      // Non-US: deal-card adds wait on this so an unchecked bundle can't be added
      this.fontanaDealsReady = this.loadDealFontanaOos()
      this.updateBrowse()
      this.refreshCartSummary()
      this.setupHeaderOffsetSync()
      this.setupCarouselArrows()
      this.setupPills()
      this.setupFamilyRows()
      this.setupHeroFootnote()
      this.setupHeroCountdown()
    }

    setupHeroFootnote() {
      this.footnoteElement = this.querySelector('[data-sale-page-footnote]')
      // The setting's copy is the fallback for every day that has no override.
      this.footnoteDefaultText = this.footnoteElement?.textContent.trim() || ''
      this.updateHeroFootnote()
    }

    updateHeroFootnote() {
      if (!this.footnoteElement) return

      const text = CLEARANCE_FOOTNOTE_BY_WEEKDAY[getPacificWeekday()] || this.footnoteDefaultText
      if (this.footnoteElement.textContent !== text) this.footnoteElement.textContent = text
    }

    setupHeroCountdown() {
      this.countdownElement = this.querySelector('[data-sale-page-countdown]')
      if (!this.countdownElement) return

      // ISO string with its own offset, so the target is absolute rather than local.
      this.countdownEndTime = Date.parse(this.countdownElement.dataset.countdownEnd || '')
      if (Number.isNaN(this.countdownEndTime)) {
        this.countdownElement.hidden = true
        return
      }

      this.countdownUnitElements = new Map(
        Array.from(this.countdownElement.querySelectorAll('[data-countdown-unit]')).map(
          (element) => [element.dataset.countdownUnit, element]
        )
      )

      this.tickHeroCountdown()
      this.countdownIntervalId = window.setInterval(this.handleCountdownTick, 1000)
    }

    // Splits the time left into hours/minutes/seconds and repaints the digits. Hours run
    // to 47 rather than wrapping at 24, since there's no days column. Also re-resolves
    // the footnote so a day rollover doesn't need a page load.
    tickHeroCountdown() {
      const millisecondsLeft = Math.max(0, this.countdownEndTime - Date.now())
      const secondsLeft = Math.floor(millisecondsLeft / 1000)

      this.countdownElement.hidden = secondsLeft > COUNTDOWN_VISIBLE_FROM_SECONDS

      this.renderCountdownUnit('hours', Math.floor(secondsLeft / 3600))
      this.renderCountdownUnit('minutes', Math.floor(secondsLeft / 60) % 60)
      this.renderCountdownUnit('seconds', secondsLeft % 60)
      this.updateHeroFootnote()

      if (millisecondsLeft === 0) this.stopHeroCountdown()
    }

    // One cell per character: a value that grows past two digits adds a cell rather than
    // widening one, which is what keeps every cell the same size.
    renderCountdownUnit(unit, value) {
      const element = this.countdownUnitElements?.get(unit)
      if (!element) return

      const digits = String(value).padStart(2, '0')
      const cells = element.querySelectorAll('.hero-countdown__digit')

      if (cells.length !== digits.length) {
        element.replaceChildren(...Array.from(digits, buildCountdownDigitCell))
        return
      }

      cells.forEach(function paintCell(cell, index) {
        if (cell.textContent !== digits[index]) cell.textContent = digits[index]
      })
    }

    stopHeroCountdown() {
      if (!this.countdownIntervalId) return

      window.clearInterval(this.countdownIntervalId)
      this.countdownIntervalId = null
    }

    // Keeps the sticky sub-nav offset in sync with the actual browse-header height
    // so the pills pin directly below the clearance header.
    setupHeaderOffsetSync() {
      if (!this.browseHeader) return
      this.syncHeaderOffset()
      if ('ResizeObserver' in window) {
        this.headerResizeObserver = new ResizeObserver(() => this.syncHeaderOffset())
        this.headerResizeObserver.observe(this.browseHeader)
      }
      window.addEventListener('resize', this.handleHeaderOffsetResize)
    }

    syncHeaderOffset() {
      // Subtract 1px to avoid a subpixel sliver where content shows above the sub-nav.
      const height = this.browseHeader?.offsetHeight || 0
      this.style.setProperty(
        '--clearance-v3-browse-header-height',
        `${Math.max(height - 1, 0)}px`
      )
    }

    // The prev/next pair for a deal row is only worth showing when its track can
    // actually scroll. Both rows render the pair hidden, so this is what reveals it —
    // re-run on resize, because a row that overflows on mobile often fits on desktop.
    setupCarouselArrows() {
      this.carouselArrowGroups = Array.from(
        this.querySelectorAll('[data-sale-page-carousel-arrows]')
      )
      if (!this.carouselArrowGroups.length) return

      // Track → its arrow group, so a scroll can update just that row's arrows.
      this.carouselTrackGroups = new Map()
      for (const group of this.carouselArrowGroups) {
        const track = this.getCarouselTrackForArrows(group)
        if (track) this.carouselTrackGroups.set(track, group)
      }

      this.syncCarouselArrows()

      // Tracks don't bubble scroll, so one capture listener covers every row, present or later.
      if (!this.handleCarouselTrackScroll) {
        this.handleCarouselTrackScroll = this.queueCarouselArrowEdges.bind(this)
        this.addEventListener('scroll', this.handleCarouselTrackScroll, { capture: true, passive: true })
      }

      if ('ResizeObserver' in window) {
        this.carouselResizeObserver = new ResizeObserver(this.handleCarouselArrowsResize)
        for (const group of this.carouselArrowGroups) {
          const track = this.getCarouselTrackForArrows(group)
          if (track) this.carouselResizeObserver.observe(track)
        }
      }

      window.addEventListener('resize', this.handleCarouselArrowsResize)
    }

    // Each arrow names its track through aria-controls; the group takes the first one's.
    getCarouselTrackForArrows(group) {
      const arrow = group.querySelector('[data-sale-page-carousel-prev]')
      return document.getElementById(arrow?.getAttribute('aria-controls') || '')
    }

    syncCarouselArrows() {
      for (const group of this.carouselArrowGroups || []) {
        const track = this.getCarouselTrackForArrows(group)
        // A 1px tolerance keeps subpixel track widths from reading as overflow.
        group.hidden = !track || track.scrollWidth - track.clientWidth <= 1
        if (track && !group.hidden) this.syncCarouselArrowEdges(group, track)
      }
    }

    // Scroll fires many times a frame; the arrows update once per frame for each row that moved.
    queueCarouselArrowEdges(event) {
      const group = this.carouselTrackGroups?.get(event.target)
      if (!group) return

      this.pendingCarouselArrowTracks ||= new Set()
      this.pendingCarouselArrowTracks.add(event.target)
      if (this.carouselArrowsFrame) return

      this.carouselArrowsFrame = window.requestAnimationFrame(() => {
        this.carouselArrowsFrame = null
        for (const track of this.pendingCarouselArrowTracks) {
          const trackGroup = this.carouselTrackGroups.get(track)
          if (trackGroup && !trackGroup.hidden) this.syncCarouselArrowEdges(trackGroup, track)
        }
        this.pendingCarouselArrowTracks.clear()
      })
    }

    // An arrow only shows while it can move the row: prev once it has left the start, next
    // until it reaches the end. Disabled arrows fade out in featured-products-page.css.
    syncCarouselArrowEdges(group, track) {
      const maxScroll = track.scrollWidth - track.clientWidth
      // Math.abs covers right-to-left rows, where scrollLeft runs negative.
      const position = Math.abs(track.scrollLeft)
      this.setCarouselArrowEnabled(group, '[data-sale-page-carousel-prev]', position > 1)
      this.setCarouselArrowEnabled(group, '[data-sale-page-carousel-next]', position < maxScroll - 1)
    }

    setCarouselArrowEnabled(group, selector, isEnabled) {
      const arrow = group.querySelector(selector)
      if (!arrow || arrow.disabled === !isEnabled) return

      // Disabling the focused arrow would drop focus to the page; hand it to the other one,
      // which is always usable when this one runs out.
      if (!isEnabled && arrow === document.activeElement) {
        group.querySelector('.deal-arrow:not([disabled])' + `:not(${selector})`)?.focus()
      }
      arrow.disabled = !isEnabled
    }

    getStickyElementHeight(selector, includeHidden = false) {
      const element = document.querySelector(selector)
      if (!element) return 0

      const styles = window.getComputedStyle(element)
      if (styles.position !== 'sticky' && styles.position !== 'fixed') return 0

      const rect = element.getBoundingClientRect()
      if (includeHidden) return rect.height
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0

      return rect.height
    }

    getHeaderScrollOffset(targetTop) {
      const visibleHeaderHeight =
        this.getStickyElementHeight('.new-announcement-bar__wrapper.is-sticky') +
        this.getStickyElementHeight('.main-nav__wrapper.is-sticky')
      const isMobile = window.matchMedia('(max-width: 767px)').matches
      const willScrollUp = targetTop - visibleHeaderHeight < window.scrollY

      if (!isMobile || !willScrollUp) return visibleHeaderHeight

      return (
        this.getStickyElementHeight('.new-announcement-bar__wrapper.is-sticky', true) +
        this.getStickyElementHeight('.main-nav__wrapper.is-sticky', true)
      )
    }

    disconnectedCallback() {
      this.singlesAutoLoadObserver?.disconnect()
      this.removeEventListener('scroll', this.handleTileTrackScroll, true)

      for (const button of this.sizeButtons || []) {
        button.removeEventListener('click', this.handleSizeClick)
      }

      for (const button of this.waistButtons || []) {
        button.removeEventListener('click', this.handleWaistClick)
      }

      for (const button of this.lengthButtons || []) {
        button.removeEventListener('click', this.handleLengthClick)
      }

      for (const button of this.topLengthButtons || []) {
        button.removeEventListener('click', this.handleTopLengthClick)
      }

      for (const button of this.bottomSizeButtons || []) {
        button.removeEventListener('click', this.handleBottomSizeClick)
      }

      for (const button of this.addSingleButtons || []) {
        button.removeEventListener('click', this.handleSingleAdd)
      }

      for (const button of this.modalSizeButtons || []) {
        button.removeEventListener('click', this.handleModalSizeClick)
      }

      for (const button of this.modalWaistButtons || []) {
        button.removeEventListener('click', this.handleModalWaistClick)
      }

      for (const button of this.modalLengthButtons || []) {
        button.removeEventListener('click', this.handleModalLengthClick)
      }

      for (const button of this.modalTopLengthButtons || []) {
        button.removeEventListener('click', this.handleModalTopLengthClick)
      }

      for (const button of this.modalCloseButtons || []) {
        button.removeEventListener('click', this.handleCloseModal)
      }

      this.modalApplyButton?.removeEventListener('click', this.handleModalApply)
      this.sizeModalOverlay?.removeEventListener('click', this.handleCloseModal)
      this.showDealsButton?.removeEventListener('click', this.handleShowDeals)
      this.removeEventListener('click', this.handleCarouselClick)
      for (const type of ['pointerover', 'pointerdown', 'focusin'])
        this.removeEventListener(type, this.handleDealQuickViewPrefetch)
      for (const button of this.changeSizeButtons || []) {
        button.removeEventListener('click', this.handleChangeSize)
      }
      // A/B test concluded: automatic bundles Shop all cleanup disabled.
      window.removeEventListener('resize', this.handleHeaderOffsetResize)
      document.removeEventListener('cartaddsuccess', this.handleCartRefresh)
      document.removeEventListener('cartupdatesuccess', this.handleCartRefresh)
      document.removeEventListener('clearanceatcsuccess', this.handleQuickAddSuccess)
      document.removeEventListener('quick-modal-content-updated', this.handleCustomPackOnlyContent)
      if (this._modalEscHandler) {
        document.removeEventListener('keydown', this._modalEscHandler)
        this._modalEscHandler = null
      }
      this.singlesAbortController?.abort()
      this.loadMoreButton?.removeEventListener('click', this.handleLoadMoreSingles)
      this.dealMediaObserver?.disconnect()
      this.mediaPeekObserver?.disconnect()
      this.fireRimObserver?.disconnect()
      for (const swiper of this.dealMediaSwipers?.values() || []) {
        swiper.destroy(true, true)
      }
      this.dealMediaSwipers?.clear()
      this.headerResizeObserver?.disconnect()
      this.carouselResizeObserver?.disconnect()
      this.pillRowsAbortController?.abort()
      this.familyRowsObserver?.disconnect()
      if (this.handlePillsStuckSync) {
        window.removeEventListener('scroll', this.handlePillsStuckSync)
        window.removeEventListener('resize', this.handlePillsStuckSync)
      }
      window.removeEventListener('resize', this.handleCarouselArrowsResize)
      if (this.handleCarouselTrackScroll) {
        this.removeEventListener('scroll', this.handleCarouselTrackScroll, { capture: true })
      }
      window.cancelAnimationFrame(this.carouselArrowsFrame)
      this.stopHeroCountdown()
    }

    handleSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopSize = size
      this.sizeCard?.classList.add('selected')

      for (const sizeButton of this.sizeButtons) {
        const isActive = sizeButton === button
        sizeButton.classList.toggle('active', isActive)
        sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      document.dispatchEvent(
        new CustomEvent('tc:top-size-change', {
          detail: { size },
        })
      )
      this.updateBrowse()
    }

    handleWaistClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomWaist = size

      for (const btn of this.waistButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.updateBottomCardSelected()
      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.updateBrowse()
    }

    handleLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomLength = size

      for (const btn of this.lengthButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.updateBottomCardSelected()
      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.updateBrowse()
    }

    // Length is an optional refinement (unlike Top Size / Bottom Waist+Length), so it
    // doesn't factor into isAnySizeSelected()/setShowDealsReady — it just narrows what's
    // shown once the shopper has proceeded past step 1.
    handleTopLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopLength = size
      this.syncTopLengthButtons()
      this.saveSelectedSize()
      this.updateBrowse()
    }

    // Reflects selectedTopLength onto both Tops Length rows — the step 1 size card and
    // the Change Sizes modal — so the two stay in lockstep however the value was set.
    syncTopLengthButtons() {
      const groups = [this.topLengthButtons, this.modalTopLengthButtons]
      for (const group of groups) {
        for (const btn of group || []) {
          const isActive =
            this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedTopLength)
          btn.classList.toggle('active', isActive)
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        }
      }
    }

    // Modal picks are held until Apply (like the modal's other rows); step 1 picks save now.
    handleBottomSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedBottomSize = size
      this.syncBottomSizeButtons()
      if (button.hasAttribute('data-tc-modal-bottom-size-option')) return

      this.setShowDealsReady(this.isAnySizeSelected())
      this.saveSelectedSize()
      this.updateBrowse()
    }

    syncBottomSizeButtons() {
      for (const btn of this.bottomSizeButtons || []) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomSize)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }
    }

    updateBottomCardSelected() {
      const isSelected = !!(this.selectedBottomWaist && this.selectedBottomLength)
      this.bottomCard?.classList.toggle('selected', isSelected)
    }

    isAnySizeSelected() {
      return !!(
        this.selectedTopSize ||
        this.selectedBottomSize ||
        (this.selectedBottomWaist && this.selectedBottomLength)
      )
    }

    showStepTwo(shouldScroll) {
      if (this.hero) this.hero.hidden = false
      if (this.stepOne) this.stepOne.hidden = true

      if (this.stepTwo) {
        this.stepTwo.hidden = false
      }

      this.updateBrowse()
      this.refreshCartSummary()

      this.dispatchEvent(
        new CustomEvent('clearance-v3:show-deals', {
          bubbles: true,
          detail: {
            topSize: this.selectedTopSize,
          },
        })
      )

      if (shouldScroll) {
        // Pages with pills (featured-products-page-v2) reveal the step in place, right where the
        // shopper picked the size, so the page stays put; the others still jump to the top.
        const shouldStayInPlace = Boolean(this.pillRows)
        if (!shouldStayInPlace) window.scrollTo({ top: 0 })
        // The active-tab focus target is gone with the tab bar; the size bar's Update
        // Sizes button is the first control in the revealed step.
        window.requestAnimationFrame(() =>
          this.changeSizeButtons?.[0]?.focus({ preventScroll: shouldStayInPlace })
        )
      }
    }

    handleShowDeals() {
      if (!this.isAnySizeSelected()) return

      this.saveSelectedSize()
      this.showStepTwo(true)
    }

    /* A/B test concluded: automatic bundles Shop all handler retained for reference.
    handleShopAllBundles() {
      const stylesTabButton = this.tabButtons.find((button) => button.dataset.tab === 'mens')
      if (!stylesTabButton) return

      stylesTabButton.click()
      stylesTabButton.focus()
    }
    */

    // When the hero has scrolled off screen, bring the products area to the top,
    // offset by the sticky header so its top isn't hidden underneath it.
    scrollProductsAreaIntoViewIfHeroHidden() {
      if (!this.productsArea || this.isElementInViewport(this.hero)) return
      const productsTop = this.productsArea.getBoundingClientRect().top + window.scrollY
      const stickyOffset =
        this.getHeaderScrollOffset(productsTop) + (this.browseHeader?.offsetHeight || 0)
      const top = productsTop - stickyOffset
      window.scrollTo({ top, behavior: this.getScrollBehavior() })
    }

    getScrollBehavior() {
      const prefersReducedMotion =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      return prefersReducedMotion ? 'auto' : 'smooth'
    }

    isElementInViewport(element) {
      if (!element) return false
      const rect = element.getBoundingClientRect()
      return (
        rect.bottom > 0 &&
        rect.top < (window.innerHeight || document.documentElement.clientHeight)
      )
    }

    // Reflects the current page state onto the sentinel and the Load More button. Called
    // from updateBrowse and after every fetch, so it is the only place that decides
    // whether the button is on screen.
    syncSinglesPaginationState() {
      const hasNextPage = Number(this.singlesNextPage || 0) > 0
      this.hasMoreSingles = hasNextPage
      if (this.singlesSentinel) this.singlesSentinel.hidden = !hasNextPage

      if (!this.loadMoreButton) return

      const isDealsActive = this.activeTab === 'deals'
      // With auto-load the sentinel does the loading; the button is only the fallback for
      // browsers without IntersectionObserver.
      // While auto-load is paused (see loadMoreSingles) the button is the way to keep going.
      const isAutoLoading = Boolean(this.singlesAutoLoadObserver) && !this.singlesAutoLoadPaused
      this.loadMoreButton.hidden = isAutoLoading || !(isDealsActive && hasNextPage)
      this.loadMoreButton.disabled = this.isLoadingMoreSingles
      // The label is the only load feedback — there is no separate loader element.
      this.loadMoreButton.textContent = this.isLoadingMoreSingles ? 'Loading...' : 'Load More'
    }

    // Fetches the next `paginate` page of the section through the Section Rendering API and
    // appends its cards. Keeps going while a page lands nothing the shopper can actually
    // see — a page filtered out entirely by their size would otherwise leave the button
    // looking broken.
    async loadMoreSingles() {
      if (this.isLoadingMoreSingles || !this.singlesNextPage || !this.singlesGrid) return
      if (this.activeTab !== 'deals' || this.stepTwo?.hidden) return

      this.isLoadingMoreSingles = true
      this.setSinglesLoading(true)
      this.syncSinglesPaginationState()
      this.singlesAbortController = new AbortController()

      const visibleCountBefore = this.visibleSingleCount
      let nextPage = this.singlesNextPage
      // A saved size can hide most of a page. Fetching until something shows used to pull
      // in the whole collection on a fast scroll, which is enough DOM and images for iOS
      // Safari to blank photos and reload the tab, so one load fetches two pages at most.
      const maxPagesPerLoad = 2
      let pagesFetched = 0

      try {
        while (nextPage && pagesFetched < maxPagesPerLoad) {
          pagesFetched += 1
          const pageData = await this.fetchSinglesPage(
            nextPage,
            this.singlesAbortController.signal
          )
          if (!pageData.grid) {
            nextPage = 0
            break
          }

          this.appendSinglesCards(pageData.grid)
          nextPage = pageData.nextPage
          this.singlesNextPage = nextPage
          this.refreshSingleCardBindings()
          this.updateBrowse()

          if (this.visibleSingleCount > visibleCountBefore) break
        }

        this.singlesNextPage = nextPage
        // Nothing new to see: stop auto-loading and let the Load More button take over.
        this.singlesAutoLoadPaused = this.visibleSingleCount <= visibleCountBefore
        this.singlesGrid.dataset.nextPage = nextPage ? String(nextPage) : ''
        this.clearanceVariantIds = this.buildClearanceVariantIds()
        await this.refreshCartSummary()
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load more Sale Page products', error)
        }
      } finally {
        this.isLoadingMoreSingles = false
        this.setSinglesLoading(false)
        this.syncSinglesPaginationState()
        this.continueSinglesAutoLoad()
      }
    }

    // Loads the next page when the end of the grid comes within ~two screens, so shoppers
    // never meet a Load More button. The observer only fires on crossings, so after each
    // page continueSinglesAutoLoad() checks whether the sentinel is still in range.
    setupSinglesAutoLoad() {
      if (!this.singlesSentinel || !('IntersectionObserver' in window)) return

      this.singlesAutoLoadObserver = new IntersectionObserver(
        (entries) => {
          this.singlesSentinelInRange = entries.some((entry) => entry.isIntersecting)
          if (this.singlesSentinelInRange && !this.singlesAutoLoadPaused) this.loadMoreSingles()
        },
        { rootMargin: '0px 0px 1600px 0px' }
      )
      this.singlesAutoLoadObserver.observe(this.singlesSentinel)
      this.syncSinglesPaginationState()
    }

    // Column toggle (Row layout "grid-toggle"). The count lives on <html> as data-plp-cols,
    // set before first paint by the section's inline script, so the CSS lays out every shelf
    // (including rows a pill change swaps in later) without re-rendering anything here.
    setupGridToggle() {
      this.gridToggle = this.querySelector('[data-plp-cols-toggle]')
      // Swipe progress on gallery tiles; one capture listener covers every track, present or later.
      this.handleTileTrackScroll = this.syncTileDots.bind(this)
      this.addEventListener('scroll', this.handleTileTrackScroll, true)
      if (!this.gridToggle) return
      this.syncGridToggleLabel()
    }

    getGridColumns() {
      return document.documentElement.getAttribute('data-plp-cols') === '3' ? 3 : 2
    }

    syncGridToggleLabel() {
      if (!this.gridToggle) return
      this.gridToggle.setAttribute(
        'aria-label',
        `Change grid layout, currently ${this.getGridColumns()} columns`
      )
    }

    toggleGridColumns() {
      const from = this.getGridColumns()
      const to = from === 2 ? 3 : 2

      // Keep the shelf the shopper is looking at in place: note its top, switch, then
      // scroll by however much the shelves above it grew or shrank.
      const stickyBottom = this.querySelector('.fp-pills')?.getBoundingClientRect().bottom || 0
      const anchor = Array.from(this.querySelectorAll('[data-fp-rows] .fp-shelf')).find(
        (shelf) => shelf.getBoundingClientRect().bottom > stickyBottom
      )
      const anchorTop = anchor?.getBoundingClientRect().top

      // Chrome's own scroll anchoring would also correct for the height change, doubling the
      // shift; Safari has none. Turn it off for this one switch so only our correction applies.
      const root = document.documentElement
      root.style.overflowAnchor = 'none'
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => root.style.removeProperty('overflow-anchor')))

      root.setAttribute('data-plp-cols', String(to))
      try {
        localStorage.setItem('tc_plp_cols', String(to))
      } catch (error) {
        // Private mode: the choice just won't persist.
      }

      // Dense view has no swiping, so every strip shows its first photo again.
      if (to === 3) {
        for (const track of this.querySelectorAll('[data-deal-tile-track]')) {
          track.scrollLeft = 0
          this.syncTileDots({ target: track })
        }
      }

      if (anchor && anchorTop !== undefined) {
        window.scrollBy({ top: anchor.getBoundingClientRect().top - anchorTop, behavior: 'instant' })
      }

      this.syncGridToggleLabel()
      this.trackGridToggle(from, to)
    }

    trackGridToggle(from, to) {
      const detail = {
        from,
        to,
        device: window.matchMedia(CARD_SCROLL_MEDIA_QUERY).matches ? 'mobile' : 'desktop',
        collection: this.pillRows?.dataset.fpActive || window.location.pathname,
      }
      try {
        window.Shopify?.analytics?.publish?.('plp_grid_toggle', detail)
      } catch (error) {
        // Analytics must never break the toggle.
      }
      window.dataLayer?.push({ event: 'plp_grid_toggle', ...detail })
    }

    syncTileDots(event) {
      const track = event.target
      if (!(track instanceof Element) || !track.matches('[data-deal-tile-track]')) return
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1))
      // The progress bar under the photos reads this; see .deal-tile__progress-fill.
      track.parentElement?.style.setProperty('--tile-index', String(index))
    }

    // The fire rims (gallery tiles and the hero pill) spin a conic gradient with a blurred
    // glow behind it, which mobile Safari repaints every frame. Off-screen rims, including
    // tiles scrolled sideways out of the strip, get data-fire-paused so the CSS stops them.
    setupFireRimPausing() {
      const rims = this.querySelectorAll('.fp-gallery .deal-tile, .fp-tile-strip .deal-tile, .fp-fire-ring')
      if (!rims.length || !('IntersectionObserver' in window)) return

      this.fireRimObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          entry.target.toggleAttribute('data-fire-paused', !entry.isIntersecting)
        }
      })
      for (const rim of rims) this.fireRimObserver.observe(rim)
    }

    continueSinglesAutoLoad() {
      if (!this.singlesAutoLoadObserver || !this.singlesSentinelInRange || !this.singlesNextPage) return
      if (this.singlesAutoLoadPaused) return
      // A short pause between pages keeps a fast scroll from firing requests back to back.
      window.clearTimeout(this.singlesAutoLoadTimer)
      this.singlesAutoLoadTimer = window.setTimeout(() => {
        const rect = this.singlesSentinel.getBoundingClientRect()
        if (rect.top < window.innerHeight + 1600) this.loadMoreSingles()
      }, 400)
    }

    async fetchSinglesPage(page, signal) {
      const response = await fetch(this.getSinglesPageUrl(page), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
      if (!response.ok) throw new Error(`Unable to fetch Sale Page page ${page}`)

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const grid = doc.querySelector('[data-clearance-v3-singles-grid]')
      const nextPage = Number(grid?.dataset.nextPage || 0)

      return { grid, nextPage }
    }

    appendSinglesCards(nextGrid) {
      const existingProductIds = new Set()
      const currentCards = Array.from(
        this.singlesGrid.querySelectorAll('[data-clearance-v3-single-card]')
      ).concat(this.getParkedSingleCards())
      for (const card of currentCards) {
        const productId = this.getSingleCardProductId(card)
        if (productId) existingProductIds.add(productId)
      }

      let appendedCount = 0
      const importedCarousels = []
      const nextCards = Array.from(nextGrid.querySelectorAll('[data-clearance-v3-single-card]'))
      for (const card of nextCards) {
        const productId = this.getSingleCardProductId(card)
        if (productId && existingProductIds.has(productId)) continue

        const importedCard = document.importNode(card, true)
        this.stripLengthIfNotEligibleForLengthFilter(importedCard)
        this.singlesGrid.appendChild(importedCard)
        importedCarousels.push(
          ...importedCard.querySelectorAll('[data-sale-page-media-carousel]')
        )
        if (productId) existingProductIds.add(productId)
        appendedCount += 1
      }

      this.registerDealMediaCarousels(importedCarousels)

      return appendedCount
    }

    getSingleCardProductId(card) {
      const productId = String(card?.dataset.productId || '').trim()
      if (productId) return productId

      const script = card?.querySelector('[data-clearance-v3-single-products]')
      if (!script) return ''

      try {
        const products = JSON.parse(script.textContent || '[]')
        return products[0]?.productId ? String(products[0].productId) : ''
      } catch (error) {
        return ''
      }
    }

    // Prefers the grid-only endpoint (sections/featured-products-singles-page.liquid) over
    // re-rendering this whole section for each page.
    getSinglesPageUrl(page) {
      const endpointTerms = this.dataset.singlesEndpointTerms
      if (endpointTerms) return this.getSinglesEndpointUrl(endpointTerms, page)

      const url = new URL(window.location.href)
      const sectionId = this.dataset.sectionId || ''
      if (sectionId) url.searchParams.set('section_id', sectionId)
      url.searchParams.set('page', String(page))
      return url.toString()
    }

    getSinglesEndpointUrl(endpointTerms, page) {
      const root = window.Shopify?.routes?.root || '/'
      const terms = encodeURIComponent(endpointTerms)
      return `${root}search?q=${terms}&type=article&section_id=featured-products-singles-page&page=${page}`
    }

    // aria-busy on the grid, plus the spinning logo under it: with auto-load there is no Load
    // More label, so the logo is what tells a shopper at the bottom that more is on the way.
    setSinglesLoading(isLoading) {
      if (this.singlesGrid) {
        if (isLoading) {
          this.singlesGrid.setAttribute('aria-busy', 'true')
        } else {
          this.singlesGrid.removeAttribute('aria-busy')
        }
      }
      this.singlesLoader ||= this.querySelector('[data-singles-loader]')
      if (this.singlesLoader) this.singlesLoader.hidden = !isLoading
    }

    handleChangeSize() {
      // The "Update Sizes" editor lets shoppers adjust both tops and bottoms.
      this.openSizeModal(true)
    }

    async openCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer')
      if (cartDrawer) {
        if (typeof cartDrawer.open !== 'function' && window.customElements?.whenDefined) {
          try {
            await Promise.race([
              window.customElements.whenDefined('cart-drawer'),
              new Promise((resolve) => window.setTimeout(resolve, 500)),
            ])
          } catch (error) {
            console.warn('Unable to wait for cart drawer definition', error)
          }
        }

        if (typeof cartDrawer.open === 'function') {
          try {
            cartDrawer.open()
            return
          } catch (error) {
            console.warn('Unable to open cart drawer', error)
          }
        }
      }

      const cartTrigger = document.querySelector('#cart-icon-bubble, #mini-cart-open')
      if (cartTrigger) {
        cartTrigger.click()
        return
      }

      window.location.href = `${this.routesRoot()}cart`
    }

    openSizeModal(showBottoms = false) {
      if (this.modalBottomsSection) this.modalBottomsSection.hidden = !showBottoms

      const allSingles = this.parseAllProducts()?.singles || []

      for (const button of this.modalSizeButtons) {
        const size = button.dataset.size || ''
        if (allSingles.length > 0) {
          const hasStock = allSingles.some((product) =>
            this.doesProductHaveTopSizeStock(product, size)
          )
          button.hidden = !hasStock
        }
        const isActive = this.normalizeSize(size) === this.normalizeSize(this.selectedTopSize)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }
      this.syncBottomSizeButtons()

      for (const button of this.modalWaistButtons) {
        const isActive =
          this.normalizeSize(button.dataset.size) === this.normalizeSize(this.selectedBottomWaist)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const button of this.modalLengthButtons) {
        const isActive =
          this.normalizeSize(button.dataset.size) ===
          this.normalizeSize(this.selectedBottomLength)
        button.classList.toggle('active', isActive)
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const button of this.modalTopLengthButtons) {
        const size = button.dataset.size || ''
        if (allSingles.length > 0) {
          const hasStock = allSingles.some(
            (product) =>
              product.available &&
              window.TcSizePreferences.getSizeCategoryForProductType(product.productType) ===
                'top' &&
              product.hasAlternateLength &&
              this.normalizeSize(product.length) === this.normalizeSize(size)
          )
          button.hidden = !hasStock
        }
      }
      this.syncTopLengthButtons()

      this.sizeModal?.classList.add('open')
      this.sizeModalOverlay?.classList.add('open')

      // a11y: expose the dialog, hide the page behind it, move focus in
      if (this.sizeModal) {
        this._modalReturnFocusEl = document.activeElement
        this.sizeModal.inert = false
        this.sizeModal.removeAttribute('aria-hidden')
        this.setPageInertForModal(true)
        this._modalEscHandler = (evt) => this.handleModalKeydown(evt)
        document.addEventListener('keydown', this._modalEscHandler)
        this.getModalFocusableElements()[0]?.focus()
      }
    }

    closeSizeModal(restoreFocus = true) {
      const shouldRestoreFocus = restoreFocus !== false

      // a11y: restore the page before moving focus out of the dialog.
      if (this.sizeModal) {
        this.setPageInertForModal(false)
        if (this._modalEscHandler) {
          document.removeEventListener('keydown', this._modalEscHandler)
          this._modalEscHandler = null
        }
        if (
          shouldRestoreFocus &&
          this._modalReturnFocusEl?.isConnected &&
          this._modalReturnFocusEl?.focus
        ) {
          this._modalReturnFocusEl.focus()
        }
        this.sizeModal.inert = true
        this.sizeModal.setAttribute('aria-hidden', 'true')
        this._modalReturnFocusEl = null
      }

      this.sizeModal?.classList.remove('open')
      this.sizeModalOverlay?.classList.remove('open')
    }

    // Inert everything at the modal's level except the modal + overlay while open
    setPageInertForModal(isOpen) {
      const siblings = this.sizeModal?.parentElement?.children || []
      for (const el of siblings) {
        if (el === this.sizeModal || el === this.sizeModalOverlay) continue
        el.inert = isOpen
      }
    }

    getModalFocusableElements() {
      if (!this.sizeModal) return []

      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')

      return Array.from(this.sizeModal.querySelectorAll(selector)).filter(
        (element) => !element.hidden && element.getClientRects().length > 0
      )
    }

    handleModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeSizeModal()
        return
      }

      if (event.key !== 'Tab') return

      const focusableElements = this.getModalFocusableElements()
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      if (!firstElement || !lastElement) {
        event.preventDefault()
        this.sizeModal?.focus()
        return
      }

      if (!this.sizeModal?.contains(document.activeElement)) {
        event.preventDefault()
        firstElement.focus()
        return
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    // Numeric-sizing cards (bottoms) show "<waist> / <length>"; letter-sized bottoms show
    // the Bottoms size; others show the top size.
    getPreferredSizeLabel(card) {
      const product = this.parseProducts(card, '[data-clearance-v3-single-products]')[0]
      if (product && this.isKidsProduct(product)) return ''
      if (product && this.getSetTopSizeOptionIndex(product) >= 0)
        return [this.selectedTopSize, this.selectedBottomSize].filter(Boolean).join(' / ')
      if (product && this.getBottomSizeOptionIndex(product) >= 0) return this.selectedBottomSize
      if (card.dataset.isNumericSizing !== 'true') return this.selectedTopSize
      return [this.selectedBottomWaist, this.selectedBottomLength].filter(Boolean).join(' / ')
    }

    // The variant getVariantForSelectedSize resolves isn't always in the shopper's preferred
    // size — a single-variant product matches unconditionally, and a single-option bottom
    // matches on waist alone — so the card labels the variant that would actually be added
    // rather than the selection. With no resolvable variant (the "Unavailable in <size>"
    // case) there's nothing to name but the size the shopper asked for.
    getCardSizeLabel(card) {
      const product = this.parseProducts(card, '[data-clearance-v3-single-products]')[0]
      const variant = product ? this.getVariantForSelectedSize(product) : null
      return (
        (variant && this.getVariantSizeLabel(variant, product)) ||
        this.getPreferredSizeLabel(card)
      )
    }

    // Option values joined the same way the preferred bottoms label is
    // ("<waist> / <length>"); tops carry a single size option so they come out as the bare
    // size. Shopify's "Default Title" placeholder and any color option are dropped so only
    // sizing is named. Falls back to the variant title, then to '' so the caller can drop
    // back to the preferred label.
    getVariantSizeLabel(variant, product) {
      const color = this.normalizeSize(product?.color || '')
      const options = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .filter((option) => {
          const normalized = this.normalizeSize(option)
          return normalized !== 'DEFAULTTITLE' && (!color || normalized !== color)
        })
      if (options.length) return options.join(' / ')

      const title = variant.title || ''
      return this.normalizeSize(title) === 'DEFAULTTITLE' ? '' : title
    }

    // The SSR card snippet always renders a computed data-length regardless of product
    // type or family. Strip it unless the product is both (a) classified 'top' by
    // TcSizePreferences and (b) belongs to a family that actually has a sibling
    // Short/Regular/Tall length (data-has-alternate-length, mirroring the
    // alternative_length_family check in snippets/your-cut-selector.liquid) — a top
    // with no alternate-length sibling has no real Length choice to filter by.
    stripLengthIfNotEligibleForLengthFilter(card) {
      const isTop =
        window.TcSizePreferences.getSizeCategoryForProductType(card.dataset.productType) === 'top'
      const hasAlternateLength = card.dataset.hasAlternateLength === 'true'
      if (card.dataset.length && (!isTop || !hasAlternateLength)) {
        delete card.dataset.length
      }
    }

    // Nothing picked means Regular: the Tops Length row starts with no button
    // preselected, but filtering still behaves as though Regular were chosen.
    getEffectiveTopLength() {
      return this.selectedTopLength || 'Regular'
    }

    // The Short/Regular/Tall length filter only applies to tops that actually have a
    // sibling length family (see stripLengthIfNotEligibleForLengthFilter, which already
    // strips data-length from anything ineligible — either not classified 'top' by
    // TcSizePreferences, or a top with no alternate-length sibling to filter between).
    // The empty-cardLength check below is what makes both cases fall through here.
    // effectiveLength lets a caller filter against a resolved length (see
    // resolveSingleLengthFilter) instead of the shopper's raw selection.
    matchesTopLengthFilter(card, effectiveLength) {
      const cardLength = card.dataset.length || ''
      if (!cardLength) return true
      const target = effectiveLength || this.getEffectiveTopLength()
      return this.normalizeSize(cardLength) === this.normalizeSize(target)
    }

    // Last Call Clearance must never bottom out at zero cards: if nothing in the shopper's Tops
    // Length is orderable in their size, fall back to Regular. The grid is DOM-paginated,
    // so a strict filter is only provisional while pages remain — an empty-looking grid
    // keeps the sentinel in view, which drains the remaining pages, and every appended
    // page re-runs updateBrowse. Deciding early would swap Regular cards out from under
    // the shopper the moment a matching page landed.
    resolveSingleLengthFilter(singleStates) {
      const selected = this.normalizeSize(this.getEffectiveTopLength())
      const regular = this.normalizeSize('Regular')
      if (selected === regular) return regular
      if (Number(this.singlesNextPage || 0) > 0) return selected

      const hasMatch = singleStates.some(
        (state) =>
          state.hasProducts &&
          state.isAvailable &&
          this.matchesTopLengthFilter(state.card, selected)
      )

      return hasMatch ? selected : regular
    }

    productMatchesTopLengthFilter(product) {
      if (window.TcSizePreferences.getSizeCategoryForProductType(product.productType) !== 'top')
        return true
      if (!product.hasAlternateLength) return true
      const productLength = product.length || ''
      if (!productLength) return true
      return this.normalizeSize(productLength) === this.normalizeSize(this.getEffectiveTopLength())
    }

    handleModalSizeClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const modalButton of this.modalSizeButtons) {
        const isActive = modalButton === button
        modalButton.classList.toggle('active', isActive)
        modalButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedTopSize = size
    }

    handleModalWaistClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const btn of this.modalWaistButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedBottomWaist = size
    }

    handleModalLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      for (const btn of this.modalLengthButtons) {
        const isActive = btn === button
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.selectedBottomLength = size
    }

    handleModalTopLengthClick(event) {
      const button = event.currentTarget
      const size = button.dataset.size || ''
      if (!size) return

      this.selectedTopLength = size
      this.syncTopLengthButtons()
    }

    applyModalSize() {
      for (const sizeButton of this.sizeButtons) {
        const isActive =
          this.normalizeSize(sizeButton.dataset.size) === this.normalizeSize(this.selectedTopSize)
        sizeButton.classList.toggle('active', isActive)
        sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const btn of this.waistButtons) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomWaist)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      for (const btn of this.lengthButtons) {
        const isActive =
          this.normalizeSize(btn.dataset.size) === this.normalizeSize(this.selectedBottomLength)
        btn.classList.toggle('active', isActive)
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
      }

      this.syncTopLengthButtons()
      this.syncBottomSizeButtons()

      this.sizeCard?.classList.toggle('selected', !!this.selectedTopSize)
      this.updateBottomCardSelected()
      this.saveSelectedSize()
      this.updateBrowse()
      this.closeSizeModal()
    }

    // Media remains cheap across the horizontal deal rows and paginated Last Call grid:
    // secondary images have no source and Swiper is not created until a card approaches
    // the viewport on either axis.
    setupDealMediaCarousels() {
      if ('IntersectionObserver' in window) {
        this.dealMediaObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue
              this.initializeDealMediaCarousel(entry.target)
              this.dealMediaObserver?.unobserve(entry.target)
            }
          },
          { rootMargin: '300px' }
        )
      }

      this.registerDealMediaCarousels(this.dealMediaCarousels || [])
      this.armTrendingMediaPeek()
    }

    // Sneak peek on the first Trending Deals card gallery, mirroring the collection
    // grid's first card (startSliderEffect in assets/global.js): once the gallery is in
    // view it slides part-way to the next image and settles back, hinting it swipes.
    // Plays once per page view, on sections/featured-products-page.liquid only
    // (sections/featured-products-page.liquid shares this element).
    shouldPlayMediaPeek() {
      if (!this.classList.contains('featured-products-page')) return false
      if (!('IntersectionObserver' in window)) return false
      return !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    }

    observeMediaPeek(carousel) {
      // Fractional card widths can keep the ratio just under 1, so 1 may never fire.
      this.mediaPeekObserver ||= new IntersectionObserver(this.handleMediaPeekIntersect.bind(this), {
        threshold: 0.9,
      })
      this.mediaPeekObserver.observe(carousel)
    }

    // Trending Deals is server-rendered and never filtered, so its first card is fixed.
    // Without that row (the deal-cards layout), the peek goes to the first swipeable gallery
    // further down instead.
    armTrendingMediaPeek() {
      if (!this.shouldPlayMediaPeek()) return

      const firstCard = this.querySelector('.hot-deals [data-sale-page-deal-card]')
      if (!firstCard) {
        this.armFirstGalleryPeek()
        return
      }
      // A single-image first card has no carousel, so it gets no peek.
      const carousel = firstCard.querySelector('[data-sale-page-media-carousel]')
      if (carousel) this.observeMediaPeek(carousel)
    }

    // The first gallery in page order with more than one photo: a gallery tile's scroll-snap
    // strip (shelf cards with the column toggle) or a Swiper carousel (carousel rows, clearance
    // cards). The dense 3-column view doesn't swipe, so a strip there gets no peek.
    armFirstGalleryPeek() {
      const gallery = Array.from(
        this.querySelectorAll('[data-deal-tile-track], [data-sale-page-media-carousel]')
      ).find((element) =>
        element.matches('[data-deal-tile-track]')
          ? element.children.length > 1
          : element.querySelectorAll('[data-sale-page-media-slide]').length > 1
      )
      if (!gallery) return

      if (!gallery.matches('[data-deal-tile-track]')) {
        this.observeMediaPeek(gallery)
        return
      }
      if (this.getGridColumns() === 3) return

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return
          observer.disconnect()
          this.peekTileTrack(gallery)
        },
        { threshold: 0.9 }
      )
      observer.observe(gallery)
    }

    // Same motion as peekMediaCarousel (~100px toward the next photo, then back), done as a
    // transform on the slides so it never fights the strip's scroll snapping.
    async peekTileTrack(track) {
      const nextImage = track.children[1]?.querySelector('img')
      if (nextImage) nextImage.loading = 'eager'
      await Promise.race([
        nextImage?.decode?.().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ])

      // Skip when the shopper has already swiped or switched to the dense view.
      if (track.scrollLeft > 0 || this.getGridColumns() === 3) return

      for (const slide of track.children) {
        slide.animate(
          [
            { transform: 'translateX(0)' },
            { transform: 'translateX(-100px)', offset: 0.73 },
            { transform: 'translateX(0)' },
          ],
          { duration: 550, easing: 'ease-in-out' }
        )
      }
    }

    handleMediaPeekIntersect(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue

        this.mediaPeekObserver.unobserve(entry.target)
        this.peekMediaCarousel(entry.target)
      }
    }

    async peekMediaCarousel(carousel) {
      this.initializeDealMediaCarousel(carousel)
      const swiper = this.dealMediaSwipers.get(carousel)
      if (!swiper) return

      // initializeDealMediaCarousel only just gave the second image its src; wait for it
      // (briefly) so the peek reveals a photo rather than an empty slide.
      const nextImage = carousel.querySelectorAll('[data-sale-page-media-slide] img')[1]
      await Promise.race([
        nextImage?.decode?.().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 800)),
      ])

      // Skip when the shopper has already moved the gallery or it has been torn down.
      if (swiper.destroyed || swiper.activeIndex !== 0) return

      swiper.slideTo(1, 400)
      swiper.setTranslate(-100)
      swiper.once('transitionEnd', function settleMediaCarousel() {
        if (!swiper.destroyed) swiper.slideTo(0, 150)
      })
    }

    registerDealMediaCarousels(carousels) {
      const pendingCarousels = Array.from(carousels).filter(
        (carousel) => carousel.querySelectorAll('[data-sale-page-media-slide]').length > 1
      )
      if (!pendingCarousels.length) return

      if (!this.dealMediaObserver) {
        for (const carousel of pendingCarousels) this.initializeDealMediaCarousel(carousel)
        return
      }

      for (const carousel of pendingCarousels) this.dealMediaObserver.observe(carousel)
    }

    initializeDealMediaCarousel(carousel) {
      if (carousel.dataset.salePageMediaInitialized === 'true') return

      if (typeof Swiper !== 'function') {
        for (const control of carousel.querySelectorAll(
          '[data-sale-page-media-prev], [data-sale-page-media-next]'
        )) {
          control.hidden = true
        }
        return
      }

      // Only the next photo loads now; syncSlides keeps one slide ahead as the shopper swipes.
      this.loadDealMediaSlide(carousel, 1)

      const previousButton = carousel.querySelector('[data-sale-page-media-prev]')
      const nextButton = carousel.querySelector('[data-sale-page-media-next]')
      const pagination = carousel.querySelector('[data-sale-page-media-pagination]')
      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

      const swiper = new Swiper(carousel, {
        direction: 'horizontal',
        slidesPerView: 1,
        loop: false,
        nested: true,
        preventClicks: true,
        preventClicksPropagation: true,
        speed: prefersReducedMotion ? 0 : 250,
        watchOverflow: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        navigation: {
          prevEl: previousButton,
          nextEl: nextButton,
        },
        pagination: {
          el: pagination,
          type: 'progressbar',
        },
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous product image',
          nextSlideMessage: 'Next product image',
        },
      })

      const syncSlides = () => {
        this.syncDealMediaSlides(carousel, swiper.activeIndex)
        this.loadDealMediaSlide(carousel, swiper.activeIndex)
        this.loadDealMediaSlide(carousel, swiper.activeIndex + 1)
      }
      swiper.on('slideChange', syncSlides)
      syncSlides()

      carousel.dataset.salePageMediaInitialized = 'true'
      this.dealMediaSwipers.set(carousel, swiper)
    }

    // Gives one gallery slide its photo. Loading every card's whole gallery as it neared the
    // screen meant hundreds of decoded photos on a long grid, which iOS Safari can't hold.
    loadDealMediaSlide(carousel, index) {
      const slide = carousel.querySelectorAll('[data-sale-page-media-slide]')[index]
      const image = slide?.querySelector('[data-sale-page-lazy-media]')
      if (!image) return

      if (image.dataset.srcset) image.srcset = image.dataset.srcset
      if (image.dataset.src) image.src = image.dataset.src
      image.removeAttribute('data-srcset')
      image.removeAttribute('data-src')
      image.removeAttribute('data-sale-page-lazy-media')
    }

    syncDealMediaSlides(carousel, activeIndex) {
      const slides = carousel.querySelectorAll('[data-sale-page-media-slide]')
      for (const [index, slide] of slides.entries()) {
        const isActive = index === activeIndex
        slide.toggleAttribute('aria-hidden', !isActive)
        slide.querySelector(
          '[data-sale-page-deal-quick-view], [data-clearance-v3-quick-add]'
        )?.setAttribute('tabindex', isActive ? '0' : '-1')
      }
    }

    // One delegated listener for both carousel rows — Hottest Deals and every
    // Staff-Pick category row. Bound on the section root because the rows are siblings;
    // it only acts on its own hooks, so the rest of the page is unaffected.
    handleCarouselClick(event) {
      const target = event.target
      if (!(target instanceof Element)) return

      const arrow = target.closest(
        '[data-sale-page-carousel-prev], [data-sale-page-carousel-next]'
      )
      if (arrow) {
        this.scrollCarousel(arrow)
        return
      }

      const colsToggle = target.closest('[data-plp-cols-toggle]')
      if (colsToggle) {
        this.toggleGridColumns()
        return
      }

      const departmentButton = target.closest('button[data-fp-dept]')
      if (departmentButton) {
        this.selectDepartment(departmentButton.dataset.fpDept)
        return
      }

      const pillButton = target.closest('[data-fp-pill]')
      if (pillButton) {
        this.selectPill(pillButton)
        return
      }

      const menuButton = target.closest('[data-sale-page-open-menu]')
      if (menuButton) {
        event.preventDefault()
        this.openNavDrawer(menuButton)
        return
      }

      const addButton = target.closest('[data-sale-page-add-deal]')
      if (addButton) {
        this.addDealToCart(addButton)
        return
      }

      const customPackButton = target.closest('[data-fp-custom-pack-open]')
      if (customPackButton) {
        this.openCustomPack(customPackButton)
        return
      }

      const quickViewButton = target.closest('[data-sale-page-deal-quick-view]')
      if (quickViewButton) this.openDealQuickView(quickViewButton)
    }

    // Each arrow names its track through aria-controls, so one handler drives every row.
    scrollCarousel(arrow) {
      const track = document.getElementById(arrow.getAttribute('aria-controls') || '')
      if (!track) return

      const isPrev = arrow.hasAttribute('data-sale-page-carousel-prev')
      const step = this.getCarouselScrollStep(track)
      track.scrollBy({
        left: isPrev ? -step : step,
        behavior: this.getScrollBehavior(),
      })
    }

    // Sections that size their cards to the screen on mobile (data-card-scroll, see
    // featured-products-page-v2.liquid) move a whole number of cards per click, so the row
    // keeps landing on card edges; everything else keeps the fixed step.
    getCarouselScrollStep(track) {
      if (!this.hasAttribute('data-card-scroll')) return CAROUSEL_SCROLL_STEP
      if (!window.matchMedia(CARD_SCROLL_MEDIA_QUERY).matches) return CAROUSEL_SCROLL_STEP

      const [firstCard, secondCard] = track.children
      if (!firstCard || !secondCard) return CAROUSEL_SCROLL_STEP

      // Card width plus the gap, read from the layout so it follows the CSS.
      const cardStep = secondCard.getBoundingClientRect().left - firstCard.getBoundingClientRect().left
      if (cardStep <= 0) return CAROUSEL_SCROLL_STEP

      const visibleCards = Math.max(1, Math.floor(track.clientWidth / cardStep))
      return visibleCards * cardStep
    }

    // Pills (sections/featured-products-page-v2.liquid). The first pill's rows come with the
    // page; any other pill's rows are fetched on demand and swapped into [data-fp-rows].
    // Pages without pills never reach past the first line.
    setupPills() {
      this.pillRows = this.querySelector('[data-fp-rows]')
      if (!this.pillRows) return

      this.pillRowsCache = new Map([
        [this.getPillCacheKey(this.pillRows.dataset.fpActive), this.pillRows.innerHTML],
      ])
      this.pillRowsRequestId = 0

      this.pillsNav = this.querySelector('.fp-pills')
      this.handlePillsStuckSync = this.syncPillsStuckState.bind(this)
      window.addEventListener('scroll', this.handlePillsStuckSync, { passive: true })
      window.addEventListener('resize', this.handlePillsStuckSync)
      this.updatePillsStuckState()
      this.setupDepartments()

      // ?focus=<pill handle>: the page script keeps the products area hidden until this lands.
      const pendingPill = this.productsArea?.dataset.fpPendingPill
      if (pendingPill) this.openPendingPill(pendingPill)
    }

    // Men / Women / Kids (sections/featured-products-page-v2.liquid). Each department is its own
    // pill list over its own menu; switching shows that list and loads its first pill through
    // the same endpoint, so every department gets this layout. ?dept=women|kids opens one.
    setupDepartments() {
      this.departmentButtons = Array.from(this.querySelectorAll('[data-fp-dept]'))
      this.department = 'men'
      if (!this.departmentButtons.length || !this.pillRows) return

      const pendingDepartment = this.productsArea?.dataset.fpPendingDept
      if (!pendingDepartment) return
      // ?dept=women&focus=<pill handle>: that pill loads directly, not the first one.
      const pendingPill = this.productsArea.dataset.fpPendingDeptPill
      this.selectDepartment(pendingDepartment, { updateUrl: false, pillHandle: pendingPill })
        .finally(() => {
          this.productsArea.hidden = false
          delete this.productsArea.dataset.fpPendingDept
          delete this.productsArea.dataset.fpPendingDeptPill
        })
    }

    async selectDepartment(department, { updateUrl = true, pillHandle = '' } = {}) {
      const list = Array.from(this.querySelectorAll('[data-fp-dept-pills]')).find(
        (item) => item.dataset.fpDeptPills === department
      )
      if (!list || department === this.department) return

      this.department = department
      this.dataset.fpActiveDept = department
      for (const button of this.departmentButtons) {
        button.setAttribute('aria-pressed', String(button.dataset.fpDept === department))
      }
      for (const item of this.querySelectorAll('[data-fp-dept-pills]')) item.hidden = item !== list

      // The men's size step only gates the men's tab; the others pick sizes in Quick Add.
      if (department !== 'men') {
        if (this.stepOne) this.stepOne.hidden = true
        if (this.stepTwo) this.stepTwo.hidden = false
      } else if (!this.isAnySizeSelected()) {
        if (this.stepTwo) this.stepTwo.hidden = true
        if (this.stepOne) this.stepOne.hidden = false
      }

      if (updateUrl) {
        const url = new URL(window.location.href)
        if (department === 'men') url.searchParams.delete('dept')
        else url.searchParams.set('dept', department)
        window.history.replaceState(window.history.state, '', url)
      }

      // A new menu: nothing is open yet, so the requested pill (else the first) loads.
      this.pillRows.dataset.fpMenu = list.dataset.fpMenu
      this.pillRows.dataset.fpActive = ''
      const pillToOpen = this.getPillButton(pillHandle) || list.querySelector('[data-fp-pill]')
      if (pillToOpen) await this.selectPill(pillToOpen, { shouldScroll: false })
      this.updatePillsStuckState()
    }

    async openPendingPill(handle) {
      const button = this.getPillButton(handle)
      try {
        if (button) await this.selectPill(button, { shouldScroll: false })
      } finally {
        this.productsArea.hidden = false
        delete this.productsArea.dataset.fpPendingPill
      }
    }

    getPillButton(handle) {
      return Array.from(this.getActivePillList().querySelectorAll('[data-fp-pill]')).find(
        (button) => button.dataset.fpPill === handle
      )
    }

    async selectPill(button, { shouldScroll = true } = {}) {
      const handle = button.dataset.fpPill
      const previousHandle = this.pillRows.dataset.fpActive
      if (!handle) return

      // Tapping the open pill has nothing to load, but a shopper scrolled down its rows
      // still expects to land back on the first one.
      if (handle === previousHandle) {
        if (shouldScroll) this.scrollToFirstPillRow()
        return
      }

      // Only the latest click may paint: an older fetch is aborted and ignored.
      this.pillRowsAbortController?.abort()
      this.pillRowsAbortController = new AbortController()
      const requestId = ++this.pillRowsRequestId

      this.markActivePill(handle)
      this.setPillRowsLoading(true)

      try {
        const html = await this.getPillRowsHtml(handle, this.pillRowsAbortController.signal)
        if (requestId !== this.pillRowsRequestId) return

        this.pillRows.innerHTML = html
        this.pillRows.dataset.fpActive = handle
        this.refreshDealRows()
        if (shouldScroll) this.scrollToFirstPillRow()
      } catch (error) {
        if (error.name === 'AbortError') return
        console.error('Unable to load Featured products pill', error)
        if (requestId === this.pillRowsRequestId) this.markActivePill(previousHandle)
      } finally {
        if (requestId === this.pillRowsRequestId) this.setPillRowsLoading(false)
      }
    }

    // Rows already visited are served from memory.
    async getPillRowsHtml(handle, signal) {
      const cacheKey = this.getPillCacheKey(handle)
      if (this.pillRowsCache.has(cacheKey)) return this.pillRowsCache.get(cacheKey)

      const root = window.Shopify?.routes?.root || '/'
      // The row layout rides along so swapped-in rows match the server-rendered first pill.
      const layout = this.pillRows.dataset.fpRowLayout
      const terms = encodeURIComponent(
        `${this.pillRows.dataset.fpMenu}~${handle}${layout ? `~${layout}` : ''}`
      )
      const url = `${root}search?q=${terms}&type=article&section_id=featured-products-pill-rows`
      const response = await fetch(url, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      if (!response.ok) throw new Error(`Unable to fetch pill rows for ${handle}`)

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html')
      const html = doc.querySelector('[data-fp-rows-payload]')?.innerHTML ?? ''
      this.pillRowsCache.set(cacheKey, html)
      return html
    }

    getPillCacheKey(handle) {
      return `${this.pillRows.dataset.fpMenu}~${handle}`
    }

    // The pill list of the department on show (the only list without a department toggle).
    getActivePillList() {
      return (
        Array.from(this.querySelectorAll('[data-fp-dept-pills]')).find(
          (list) => list.dataset.fpMenu === this.pillRows?.dataset.fpMenu
        ) || this
      )
    }

    // Family rows (snippets/featured-products-pill-rows.liquid) arrive with only their first
    // fabric card filled in. Each row fetches the rest once it comes within a screen or so,
    // through the same endpoint as the pills with the row's handle as a fourth term.
    setupFamilyRows() {
      this.familyRowsObserver?.disconnect()
      const rows = this.querySelectorAll('[data-fp-family-pending]')
      if (!rows.length || !this.pillRows) return

      this.familyRowsCache ??= new Map()
      this.familyRowsObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            this.familyRowsObserver.unobserve(entry.target)
            this.loadFamilyRow(entry.target)
          }
        },
        { rootMargin: '600px 0px' }
      )
      for (const row of rows) this.familyRowsObserver.observe(row)
    }

    async loadFamilyRow(row) {
      const rowHandle = row.dataset.fpFamilyPending
      const pillHandle = this.pillRows.dataset.fpActive
      const cacheKey = `${this.pillRows.dataset.fpMenu}~${pillHandle}~${rowHandle}`

      try {
        let html = this.familyRowsCache.get(cacheKey)
        if (html === undefined) {
          const root = window.Shopify?.routes?.root || '/'
          const layout = this.pillRows.dataset.fpRowLayout || 'grid'
          const terms = encodeURIComponent(
            `${this.pillRows.dataset.fpMenu}~${pillHandle}~${layout}~${rowHandle}`
          )
          const url = `${root}search?q=${terms}&type=article&section_id=featured-products-pill-rows`
          const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          if (!response.ok) throw new Error(`Unable to fetch family row ${rowHandle}`)

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html')
          html = doc.querySelector('[data-fp-rows-payload] [data-fp-family-track]')?.innerHTML ?? ''
          this.familyRowsCache.set(cacheKey, html)
        }

        // A pill switch while this was in flight already replaced the row.
        if (!row.isConnected) return
        const track = row.querySelector('[data-fp-family-track]')
        if (!track || !html) throw new Error(`Family row ${rowHandle} came back empty`)

        // Only the grey stand-ins are replaced. Cards already on the page stay as they are,
        // so a gallery the first-gallery peek is watching, or one mid-swipe, is never swapped
        // out from under it.
        const template = document.createElement('template')
        template.innerHTML = html
        const shownSlots = new Map(
          Array.from(track.querySelectorAll('.fp-shelf[data-fp-family]'), (shelf) => [
            shelf.dataset.fpFamily,
            shelf.closest('.fp-family__slot'),
          ])
        )
        for (const pending of track.querySelectorAll('.fp-shelf--pending')) {
          pending.closest('.fp-family__slot')?.remove()
        }
        let previousSlot = null
        for (const slot of Array.from(template.content.children)) {
          const family = slot.querySelector('[data-fp-family]')?.dataset.fpFamily
          const shownSlot = shownSlots.get(family)
          if (shownSlot) {
            previousSlot = shownSlot
            continue
          }
          if (previousSlot) previousSlot.after(slot)
          else track.prepend(slot)
          previousSlot = slot
        }
        row.removeAttribute('data-fp-family-pending')
        this.syncCarouselArrows()
        this.fontanaDealsReady = this.loadDealFontanaOos()
      } catch (error) {
        console.error('Unable to load family row', error)
        // The first card is real; drop the grey stand-ins rather than leave them spinning.
        for (const pending of row.querySelectorAll('.fp-shelf--pending')) {
          pending.closest('.fp-family__slot')?.remove()
        }
        row.removeAttribute('data-fp-family-pending')
        this.syncCarouselArrows()
      }
    }

    // The pills stick under the size bar. Flags when they are stuck so the stylesheet can
    // shrink them; throttled to one check per frame.
    syncPillsStuckState() {
      if (this.pillsStuckFrame) return
      this.pillsStuckFrame = window.requestAnimationFrame(() => {
        this.pillsStuckFrame = 0
        this.updatePillsStuckState()
      })
    }

    updatePillsStuckState() {
      if (!this.pillsNav) return

      // Hidden (step one still showing) means there is nothing to stick.
      const isVisible = this.pillsNav.offsetParent !== null
      const stickyTop = parseFloat(window.getComputedStyle(this.pillsNav).top)
      const isStuck =
        isVisible &&
        Number.isFinite(stickyTop) &&
        this.pillsNav.getBoundingClientRect().top <= stickyTop + 1
      this.pillsNav.dataset.stuck = String(isStuck)
    }

    // A shopper scrolled deep into one pill's rows would otherwise land mid-page on the next
    // ones. Brings the first row just under the sticky stack; does nothing when it is already
    // in view below it.
    scrollToFirstPillRow() {
      const firstRow = this.pillRows.firstElementChild
      if (!firstRow || !this.pillsNav) return

      this.updatePillsStuckState()
      if (this.pillsNav.dataset.stuck !== 'true') return

      const targetTop = this.pillsNav.getBoundingClientRect().bottom + PILL_ROWS_SCROLL_GAP
      const distance = firstRow.getBoundingClientRect().top - targetTop
      if (distance >= 0) return

      window.scrollBy({ top: distance, behavior: this.getScrollBehavior() })
    }

    markActivePill(handle) {
      for (const button of this.getActivePillList().querySelectorAll('[data-fp-pill]')) {
        const isActive = button.dataset.fpPill === handle
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false')
        if (isActive) this.centerPillInList(button)
      }
    }

    // Scrolls only the pills list sideways, so picking a pill never moves the page vertically
    // (scrollIntoView would, and the page scroll is handled by scrollToFirstPillRow).
    centerPillInList(button) {
      const list = button.closest('.fp-pills__list')
      if (!list) return

      const listRect = list.getBoundingClientRect()
      const buttonRect = button.getBoundingClientRect()
      const offset = buttonRect.left - listRect.left - (listRect.width - buttonRect.width) / 2
      list.scrollTo({ left: list.scrollLeft + offset, behavior: this.getScrollBehavior() })
    }

    // Same cue the PDP uses while it swaps products: dim and ignore input until it lands.
    setPillRowsLoading(isLoading) {
      this.pillRows.style.opacity = isLoading ? '0.5' : ''
      this.pillRows.style.pointerEvents = isLoading ? 'none' : ''
      this.pillRows.setAttribute('aria-busy', isLoading ? 'true' : 'false')
    }

    // Rows that arrive after load need what connectedCallback gives the first ones: media
    // carousels, overflow arrows and the Fontana stock check on their products.
    refreshDealRows() {
      for (const [carousel, swiper] of this.dealMediaSwipers) {
        if (carousel.isConnected) continue
        swiper.destroy(true, true)
        this.dealMediaSwipers.delete(carousel)
      }

      this.carouselResizeObserver?.disconnect()
      this.setupCarouselArrows()
      this.registerDealMediaCarousels(
        this.pillRows.querySelectorAll('[data-sale-page-media-carousel]')
      )
      this.fontanaDealsReady = this.loadDealFontanaOos()
      this.setupFamilyRows()
    }

    // Opens the theme's <nested-nav-menus> drawer through its own API, the same way
    // assets/breadcrumbs.js does. The trigger's data-sale-page-open-menu value is the
    // handleized row heading, passed as parentHandle so the drawer opens expanded to that
    // category; nested-nav-menus falls back to a plain open when no parent matches.
    openNavDrawer(trigger) {
      const navMenus = document.querySelector('nested-nav-menus')
      if (typeof navMenus?.openNavMenus !== 'function') {
        console.warn('Sale Page: nested-nav-menus openNavMenus not available')
        return
      }

      const parentHandle = trigger?.dataset.salePageOpenMenu
      navMenus.openNavMenus(parentHandle ? { parentHandle } : {})
    }

    // All Fontana logic is non-US only; US is untouched (same gate as liquid-pdp.js).
    isFontanaSoldOutRegion() {
      return this.dataset.country !== 'US'
    }

    getDealProducts() {
      const products = []
      for (const card of this.querySelectorAll('[data-sale-page-deal-card]'))
        products.push(...this.parseProducts(card, '[data-sale-page-deal-products]'))
      return products
    }

    // Bundle variant: Fontana-OOS when any component is (Storefront lookup). Plain
    // variant: its own flag, once the lookup confirms it isn't a bundle (liquid-pdp.js rule).
    isDealVariantFontanaOos(variant) {
      if (packVariantFontanaOos.has(variant.id)) return packVariantFontanaOos.get(variant.id)
      return plainPackVariantIds.has(variant.id) && variant.isFontanaOos === true
    }

    // Marks Fontana-OOS variants unavailable on the cached card JSON, so every
    // existing availability check (add, quick view preselect) treats them as sold out.
    applyFontanaOosToProducts(products) {
      for (const product of products)
        for (const variant of product.variants || [])
          if (this.isDealVariantFontanaOos(variant)) variant.available = false
    }

    // Non-US: looks up the deal cards' products once (liquid-pdp.js helpers, shared cache),
    // then applies the result. A failed lookup fails open, like the PDP.
    async loadDealFontanaOos() {
      if (!this.isFontanaSoldOutRegion()) return
      if (typeof ensurePackFontanaOosLoaded !== 'function') return
      const products = this.getDealProducts()
      const productIds = [...new Set(products.map((product) => product.productId))]
      if (!productIds.length) return
      await ensurePackFontanaOosLoaded(productIds, [], [])
      this.applyFontanaOosToProducts(products)
    }

    // Adds the variant matching the shopper's selected size, the same resolution the
    // Last Call Clearance cards use. Products with no match fall back to the Quick Add
    // modal rather than silently adding the wrong size.
    async addDealToCart(button) {
      const card = button.closest('[data-sale-page-deal-card]')
      if (!card) return
      await this.fontanaDealsReady

      if (!this.isDealAddDirect(card)) {
        this.openDealQuickView(button)
        return
      }

      const items = this.getSelectableItems(card, '[data-sale-page-deal-products]', false)
      if (!items.length) {
        this.openDealQuickView(button)
        return
      }

      await this.addItemsToCart(items, button, card, 'deal')
    }

    // True when Add can skip Quick Add: the saved sizes resolve a variant for every item. The
    // saved sizes are men's; elsewhere the shopper picks one in Quick Add.
    isDealAddDirect(card) {
      if (this.department && this.department !== 'men') return false
      return this.getSelectableItems(card, '[data-sale-page-deal-products]', false).length > 0
    }

    // The image, title, and "Choose Colors" use the shared Quick Add modal, preselecting
    // the size-matched variant when there is one. No show-only-clearance property: these
    // rows are merchandised separately and their products are not necessarily clearance.
    async openDealQuickView(trigger) {
      const card = trigger.closest('[data-sale-page-deal-card]')
      if (!card || typeof openQuickAddModal !== 'function') return
      const dealQuickModal = this.getDealQuickModal(trigger)
      if (dealQuickModal) this.showDealQuickModalPreview(dealQuickModal, card)
      await this.fontanaDealsReady

      const product = this.parseProducts(card, '[data-sale-page-deal-products]')[0]
      const handle = product?.handle || card.dataset.productHandle
      if (!handle) return

      const usesSavedSizes = !this.department || this.department === 'men'
      const variant = product && usesSavedSizes ? this.getVariantForSelectedSize(product) : null
      if (dealQuickModal) {
        this.openDealQuickModal(dealQuickModal, handle, variant)
        return
      }
      // A map can list only some tiles (the sale page's custom pinned tile); the rest stay standard.
      const customPackHandle = this.findTileCustomPackHandle(trigger, handle)
      if (customPackHandle) {
        this.openCustomPackOnlyQuickAdd(customPackHandle)
        return
      }
      this.openQuickAddWithSavedSizes(handle, [], variant)
    }

    // "tileHandle=packHandle," pairs from the section: the pack that opens the tile's own custom
    // 6-pack. A tile missing from the map opens on its own product.
    getCustomPackHandle(customPackMap, tileHandle) {
      return this.findCustomPackHandle(customPackMap, tileHandle) || tileHandle
    }

    // Same lookup, but '' for a tile missing from the map.
    findCustomPackHandle(customPackMap, tileHandle) {
      const pair = customPackMap.split(',').find((entry) => entry.startsWith(`${tileHandle}=`))
      return pair?.split('=')[1] || ''
    }

    // The custom-pack handle of the tile the trigger sits in, '' when its row has no map or the
    // tile isn't in it.
    findTileCustomPackHandle(trigger, tileHandle) {
      const row = trigger.closest('[data-fp-custom-pack-map]')
      if (!row) return ''
      // In a custom-pack-first row only the marked tile counts, not another tile of the same product.
      if (row.hasAttribute('data-fp-custom-pack-first') && !trigger.closest('[data-custom-pack-tile]')) return ''
      return this.findCustomPackHandle(row.dataset.fpCustomPackMap, tileHandle)
    }

    // Rows marked data-fp-custom-pack-first (the sale page's gallery with a custom pinned
    // product) open a mapped tile's custom pack even when the row opens the simplified modal.
    isCustomPackFirstTile(trigger) {
      if (!trigger.closest('[data-fp-custom-pack-first]')) return false
      const tileHandle = trigger.closest('[data-product-handle]')?.dataset.productHandle
      return Boolean(tileHandle && this.findTileCustomPackHandle(trigger, tileHandle))
    }

    // Single-row gallery tiles (data-fp-custom-pack-map): the regular Quick Add opens on the tile
    // family's 6-pack (a regular pack, not the custom-pack product itself, which has no sizes) as
    // a custom-pack builder. `custom-pack-only` makes quick-add-modal.js flag <product-info>, and
    // quick-add-modal.css then drops the quantity and product selectors. It lands in bundle mode
    // with the slots pre-filled from the opened pack's colors (revealTCustomPack). The tile's own variant doesn't
    // apply, so the saved size is restored instead. A handle with no custom-pack record stays a
    // standard Quick Add, since the CSS only applies to bundle-eligible products.
    async openCustomPackOnlyQuickAdd(handle) {
      if (typeof openQuickAddModal !== 'function') return
      document.addEventListener('quick-modal-content-updated', this.handleCustomPackOnlyContent)
      await openQuickAddModal(handle, [{ key: 'custom-pack-only', value: 'true' }], null)
    }

    // quick-modal-content-updated fires in the same task that drops the modal's loading state, so
    // the builder is in before the regular pack is ever painted. Other opens (deal modal, regular
    // Quick Add) are skipped and the listener waits for the custom-pack one.
    showCustomPackOnlyContent(event) {
      const modal = event.detail?.modal
      if (modal?.localName !== 'quick-modal' || modal.dataset.customPackOnly !== 'true') return
      document.removeEventListener('quick-modal-content-updated', this.handleCustomPackOnlyContent)

      const productInfo = modal.querySelector('product-info')
      productInfo?.restoreSizePreference?.()
      // No colors passed: the slots pre-fill from the opened pack's own colors.
      productInfo?.revealTCustomPack?.('', { shouldScroll: false })
    }

    // Rows marked data-deal-quick-modal-row (the gallery, when its setting is on) open the
    // simplified modal from snippets/deal-quick-modal.liquid. It moves itself to <body>, so it
    // is found by section id rather than inside this element.
    getDealQuickModal(trigger) {
      if (!trigger.closest('[data-deal-quick-modal-row]')) return null
      if (this.isCustomPackFirstTile(trigger)) return null
      return document.querySelector(`deal-quick-modal[data-section-id="${this.dataset.sectionId}"]`)
    }

    getDealProductHref(handle) {
      return (window.Shopify?.routes?.root || '/') + 'products/' + handle
    }

    // Same as openQuickAddWithSavedSizes, on the deal modal instead of the shared one. The
    // tile's photo and title stand in until the product is in (usually prefetched already).
    async openDealQuickModal(modal, handle, variant) {
      modal.dataset.productHref = this.getDealProductHref(handle)
      await modal.open(false, variant?.id || null)
      if (variant) return
      modal.querySelector('product-info')?.restoreSizePreference?.()
    }

    // Shows the tapped tile's photo and title in the deal modal straight away.
    showDealQuickModalPreview(modal, card) {
      const image = card.querySelector('img')
      modal.showPreview?.({
        imageSrc: image?.currentSrc || image?.src || '',
        title: card.querySelector('.deal-tile__name')?.textContent.trim() || '',
      })
    }

    // Hover, tap or focus on a deal-modal tile starts loading its product, so it is usually
    // ready by the click. Uses the same size-matched variant as openDealQuickView, so the
    // request is the one the modal makes. Custom-pack gallery tiles do the same on the shared
    // modal, for the custom pack they open.
    async prefetchDealQuickView(event) {
      const target = event.target
      if (!(target instanceof Element)) return
      const trigger = target.closest('[data-sale-page-deal-quick-view], [data-sale-page-add-deal]')
      if (!trigger) return
      const modal = this.getDealQuickModal(trigger)
      const customPackMap = trigger.closest('[data-fp-custom-pack-map]')?.dataset.fpCustomPackMap
      const card = trigger.closest('[data-sale-page-deal-card]')
      if (!card || (!modal && !customPackMap)) return

      // Repeat calls are cheap: the modal's cache keeps one request per product and size.
      await this.fontanaDealsReady
      // The tile's Add button only needs the modal when the saved sizes don't resolve a variant.
      const isAddButton = trigger.matches('[data-sale-page-add-deal]')
      if (isAddButton && this.isDealAddDirect(card)) return
      const product = this.parseProducts(card, '[data-sale-page-deal-products]')[0]
      const handle = product?.handle || card.dataset.productHandle
      if (!handle) return

      if (!modal) {
        // Unmapped tiles (a partial map) have no custom pack to load.
        const customPackHandle = this.findCustomPackHandle(customPackMap, handle)
        if (customPackHandle) this.prefetchCustomPackOnlyQuickAdd(customPackHandle)
        return
      }
      const usesSavedSizes = !this.department || this.department === 'men'
      const variant = product && usesSavedSizes ? this.getVariantForSelectedSize(product) : null
      modal.prefetch?.(this.getDealProductHref(handle), variant?.id || null)
    }

    // Same request openCustomPackOnlyQuickAdd makes: the shared modal, no variant.
    prefetchCustomPackOnlyQuickAdd(handle) {
      return document.querySelector('quick-modal')?.prefetch?.(this.getDealProductHref(handle))
    }

    // Once the page has loaded and gone idle, loads every gallery tile's custom pack in tile order,
    // one at a time so they never compete with each other or the page. A tile opened before its
    // turn still prefetches on hover/tap. Skipped when the shopper is saving data.
    async preloadCustomPackOnlyQuickAdds() {
      if (this.hasStartedCustomPackPreload) return
      const handles = this.getCustomPackOnlyHandles()
      if (!handles.length || this.isSavingData()) return
      this.hasStartedCustomPackPreload = true

      await waitForPageLoad()
      for (const handle of handles) {
        await waitForIdle()
        if (!this.isConnected) return
        await this.prefetchCustomPackOnlyQuickAdd(handle)?.catch(() => null)
      }
    }

    // Unique custom-pack handles from the gallery's "tileHandle=packHandle," map, in tile order.
    getCustomPackOnlyHandles() {
      const customPackMap = this.querySelector('[data-fp-custom-pack-map]')?.dataset.fpCustomPackMap
      if (!customPackMap) return []
      const tileHandles = customPackMap
        .split(',')
        .filter(Boolean)
        .map((entry) => entry.split('=')[0])
      return [...new Set(tileHandles.map((tileHandle) => this.getCustomPackHandle(customPackMap, tileHandle)))]
    }

    isSavingData() {
      const connection = navigator.connection
      if (!connection) return false
      return connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '')
    }

    // Custom Pack tile (snippets/featured-products-family-shelf.liquid): the Quick Add modal on
    // the pack, then the PDP's own "Customize" path, which empties the slots and enters
    // bundle mode. A product the PDP doesn't offer Custom Pack for just stays on the pack.
    async openCustomPack(trigger) {
      const handle = trigger.dataset.fpCustomPackOpen
      if (!handle || typeof openQuickAddModal !== 'function') return
      await this.fontanaDealsReady
      await this.openQuickAddWithSavedSizes(handle, [], null)
      document.querySelector('quick-modal product-info')?.revealTCustomPackEmpty?.()
    }

    // With no fully matched variant (e.g. a Tops + Bottoms set with only one size saved),
    // runs the PDP's own restore so the saved option is still picked and the other left
    // blank. The modal reuses one <product-info>, so its connect-time restore never re-runs.
    async openQuickAddWithSavedSizes(handle, dataAttributes, variant) {
      if (typeof openQuickAddModal !== 'function') return
      await openQuickAddModal(handle, dataAttributes, variant?.id || null)
      if (variant) return
      document.querySelector('quick-modal product-info')?.restoreSizePreference?.()
    }

    async handleSingleAdd(event) {
      const button = event.currentTarget
      const card = button.closest('[data-clearance-v3-single-card]')
      const items = this.getSelectableItems(card, '[data-clearance-v3-single-products]', false)
      if (!items.length && this.openPartialSizeQuickAdd(card)) return
      await this.addItemsToCart(items, button, card, 'single')
    }

    // Returns true when it opened the Quick Add for a kids product or a partially sized set.
    openPartialSizeQuickAdd(card) {
      const product = this.parseProducts(card, '[data-clearance-v3-single-products]')[0]
      if (!product || !this.shouldPickSizeInQuickAdd(product)) return false

      const handle = product.handle || card.dataset.productHandle
      if (!handle) return false
      this.openQuickAddWithSavedSizes(handle, [{ key: 'show-only-clearance', value: 'true' }], null)
      return true
    }

    setShowDealsReady(isReady) {
      if (!this.showDealsButton) return

      this.showDealsButton.disabled = !isReady
      this.showDealsButton.classList.toggle('ready', isReady)
      this.showDealsButton.setAttribute('aria-disabled', isReady ? 'false' : 'true')
    }

    // Where this section persists the shopper's size. Defaults to the sitewide key.
    sizeStorageKey() {
      return this.dataset.sizeStorageKey || DEFAULT_SIZE_STORAGE_KEY
    }

    saveSelectedSize() {
      try {
        const selection = {
          top: this.selectedTopSize,
          bottomWaist: this.selectedBottomWaist,
          bottomLength: this.selectedBottomLength,
          topLength: this.selectedTopLength,
          bottomSize: this.selectedBottomSize,
        }
        const key = this.sizeStorageKey()
        let sizes

        if (key === DEFAULT_SIZE_STORAGE_KEY) {
          sizes = window.TcSizePreferences.commitSizePreference(selection)
        } else {
          // A scoped key is stored on its own, deliberately bypassing
          // TcSizePreferences: commitSizePreference also pushes to Klaviyo and syncs the
          // cart attribute, which together are the shopper's one sitewide sizing
          // profile. A women's-only pick has no business overwriting that.
          const stored = JSON.parse(window.localStorage.getItem(key) || '{}')
          sizes = Object.assign({}, stored, selection)
          window.localStorage.setItem(key, JSON.stringify(sizes))
        }

        document.dispatchEvent(
          new CustomEvent('tc:sizes-saved', {
            detail: { sizes },
          })
        )
      } catch (error) {
        console.warn('Unable to save clearance size selection', error)
      }
    }

    restoreSelectedSize() {
      try {
        const rawSizes = window.localStorage.getItem(this.sizeStorageKey())
        if (!rawSizes) return

        const sizes = JSON.parse(rawSizes)
        const topSize = sizes?.top || ''
        const bottomWaist = sizes?.bottomWaist || ''
        const bottomLength = sizes?.bottomLength || ''
        const topLength = sizes?.topLength || ''
        const bottomSize = sizes?.bottomSize || ''

        if (topSize) {
          const selectedButton = this.sizeButtons.find(
            (button) => this.normalizeSize(button.dataset.size) === this.normalizeSize(topSize)
          )
          if (selectedButton) {
            this.selectedTopSize = selectedButton.dataset.size || topSize
            this.sizeCard?.classList.add('selected')
            for (const sizeButton of this.sizeButtons) {
              const isActive = sizeButton === selectedButton
              sizeButton.classList.toggle('active', isActive)
              sizeButton.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        if (bottomWaist) {
          const waistBtn = this.waistButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomWaist)
          )
          if (waistBtn) {
            this.selectedBottomWaist = waistBtn.dataset.size || bottomWaist
            for (const btn of this.waistButtons) {
              const isActive = btn === waistBtn
              btn.classList.toggle('active', isActive)
              btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        if (bottomLength) {
          const lengthBtn = this.lengthButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomLength)
          )
          if (lengthBtn) {
            this.selectedBottomLength = lengthBtn.dataset.size || bottomLength
            for (const btn of this.lengthButtons) {
              const isActive = btn === lengthBtn
              btn.classList.toggle('active', isActive)
              btn.setAttribute('aria-pressed', isActive ? 'true' : 'false')
            }
          }
        }

        // No stored value leaves it unset (no button preselected); getEffectiveTopLength()
        // still filters as 'Regular'.
        if (topLength) {
          const topLengthBtn = this.modalTopLengthButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(topLength)
          )
          if (topLengthBtn) {
            this.selectedTopLength = topLengthBtn.dataset.size || topLength
          }
        }
        this.syncTopLengthButtons()

        if (bottomSize) {
          const bottomSizeBtn = this.bottomSizeButtons.find(
            (b) => this.normalizeSize(b.dataset.size) === this.normalizeSize(bottomSize)
          )
          if (bottomSizeBtn) this.selectedBottomSize = bottomSizeBtn.dataset.size || bottomSize
        }
        this.syncBottomSizeButtons()

        this.updateBottomCardSelected()

        if (this.isAnySizeSelected()) {
          this.setShowDealsReady(true)
          this.showStepTwo(false)
        }
      } catch (error) {
        console.warn('Unable to restore clearance size selection', error)
      }
    }

    parseTiers() {
      // The shared resolver lives in snippets/global-js.liquid, inline in the
      // head ahead of this deferred script. Guard anyway: if that script is ever
      // missing — a partial deploy, or a `theme dev` sync that lands assets
      // before snippets — the section should degrade, not throw and die.
      const fallbackTiers =
        typeof getClearanceDiscountTierList === 'function'
          ? getClearanceDiscountTierList()
          : [{ quantity: 1, percentage: 25 }]
      const script = this.querySelector('[data-clearance-v3-tiers]')
      if (!script) return fallbackTiers

      try {
        const tiers = JSON.parse(script.textContent || '[]')
          .map((tier) => ({
            quantity: Number(tier.quantity),
            percentage: Number(tier.percentage),
          }))
          .filter((tier) => Number.isFinite(tier.quantity) && tier.quantity > 0)
          .sort((a, b) => a.quantity - b.quantity)

        return tiers.length ? tiers : fallbackTiers
      } catch (error) {
        console.warn('Unable to parse clearance tiers', error)
        return fallbackTiers
      }
    }

    // Base discount applies to all clearance items before any quantity tier is reached.
    parseBaseDiscount() {
      const parsed = Number(this.dataset.baseDiscount)
      if (Number.isFinite(parsed) && parsed >= 0) return parsed
      return typeof getClearanceBaseDiscount === 'function' ? getClearanceBaseDiscount() : 25
    }

    buildClearanceVariantIds() {
      const ids = new Set(this.clearanceVariantIds || [])
      for (const card of this.singleCards || []) {
        if (card.dataset.clearanceTagged !== 'true') continue
        for (const product of this.parseProducts(card, '[data-clearance-v3-single-products]')) {
          for (const variant of product.variants || []) {
            if (variant.id) ids.add(Number(variant.id))
          }
        }
      }
      return ids
    }

    parseAllProducts() {
      const script = this.querySelector('[data-clearance-products]')
      if (!script) return null
      try {
        return JSON.parse(script.textContent || '{}')
      } catch (e) {
        console.warn('Unable to parse clearance products data', e)
        return null
      }
    }

    /* A/B test concluded: automatic bundle generation retained for reference.
    renderAutomaticBundles() {
      if (this.isFetchingSingles || !this.automaticBundlesGrid) return

      const singles = this.parseAllProducts()?.singles || []
      const bundles = []
      const maxAutomaticBundles = 10
      const seenPdpAppIds = new Set()

      for (const subRow of this.querySelectorAll('[data-clearance-v3-style-row]')) {
        const pdpAppId = String(subRow.dataset.pdpAppId || '')
        if (!pdpAppId || seenPdpAppIds.has(pdpAppId) || !this.isStyleBundleEnabled(subRow)) {
          continue
        }

        const products = singles.filter(
          (product) => String(product.pdpAppId) === pdpAppId
        )
        const bundle = this.buildStyleBundle(subRow, products)
        if (!bundle) continue

        seenPdpAppIds.add(pdpAppId)
        bundles.push(bundle)
      }

      const visibleBundles = bundles.slice(0, maxAutomaticBundles)
      if (this.automaticBundlesShopAll) {
        this.automaticBundlesShopAll.hidden = bundles.length <= maxAutomaticBundles
      }

      const signature = visibleBundles
        .map(
          (bundle) =>
            `${bundle.name}:${bundle.products.map((product) => product.productId).join(',')}`
        )
        .join('|')
      if (this.automaticBundlesGrid.dataset.bundleSignature === signature) {
        this.setAutomaticBundlesLoading(false)
        return
      }

      const fragment = document.createDocumentFragment()
      for (const bundle of visibleBundles) {
        fragment.appendChild(this.createPackCardElement(bundle, 'automatic'))
      }

      this.automaticBundlesGrid.replaceChildren(fragment)
      this.automaticBundlesGrid.dataset.bundleSignature = signature
      this.setAutomaticBundlesLoading(false)
      if (this.automaticBundlesEmpty) this.automaticBundlesEmpty.hidden = bundles.length > 0

      this.refreshPackCardBindings()
      this.clearanceVariantIds = this.buildClearanceVariantIds()
      this.syncCardCartState(this.cartVariantIds, this.cartVariantQuantities)
    }

    setAutomaticBundlesLoading(isLoading) {
      if (this.automaticBundlesLoading) this.automaticBundlesLoading.hidden = !isLoading
      if (this.automaticBundlesGrid) {
        this.automaticBundlesGrid.setAttribute('aria-busy', isLoading ? 'true' : 'false')
      }
    }
    */

    refreshSingleCardBindings() {
      this.singleCards = Array.from(
        this.querySelectorAll('[data-clearance-v3-single-card]')
      ).concat(this.getParkedSingleCards())
      for (const card of this.singleCards) this.stripLengthIfNotEligibleForLengthFilter(card)
      this.addSingleButtons = Array.from(this.querySelectorAll('[data-clearance-v3-add-single]'))

      for (const button of this.addSingleButtons) {
        button.removeEventListener('click', this.handleSingleAdd)
        button.addEventListener('click', this.handleSingleAdd)
      }
    }

    parseProducts(card, selector) {
      if (!card) return []

      if (!card.clearanceV3Products) {
        card.clearanceV3Products = {}
      }

      if (card.clearanceV3Products[selector]) {
        return card.clearanceV3Products[selector]
      }

      const script = card.querySelector(selector)
      if (!script) return []

      try {
        const products = JSON.parse(script.textContent || '[]')
        card.clearanceV3Products[selector] = products
        return products
      } catch (error) {
        console.warn('Unable to parse clearance products', error)
        card.clearanceV3Products[selector] = []
        return []
      }
    }

    getSelectableItems(card, selector, requireAllProducts) {
      const products = this.parseProducts(card, selector)
      if (!products.length) return []

      const items = []
      for (const product of products) {
        if (!this.isProductAvailable(product)) {
          if (requireAllProducts) return []
          continue
        }

        const variant = this.getVariantForSelectedSize(product)
        if (!variant && requireAllProducts) return []
        if (variant) items.push({ product, variant })
      }

      return items
    }

    hasSelectableItems(card, selector, requireAllProducts) {
      const products = this.parseProducts(card, selector)
      if (!products.length) return false

      let selectableCount = 0
      for (const product of products) {
        if (!this.isProductAvailable(product)) continue

        if (this.getVariantForSelectedSize(product) || this.shouldPickSizeInQuickAdd(product)) {
          selectableCount += 1
        }
      }

      return requireAllProducts ? selectableCount === products.length : selectableCount > 0
    }

    isProductAvailable(product) {
      if (product?.available === false) return false

      const variants = Array.isArray(product?.variants) ? product.variants : []
      return variants.some((variant) => variant.available)
    }

    getVariantForSelectedSize(product) {
      if (!this.isProductAvailable(product)) return null

      const variants = Array.isArray(product?.variants) ? product.variants : []
      if (!variants.length) return null

      if (variants.length === 1) {
        const variant = variants[0]
        if (variant.available) return variant
      }

      // Kids sizes are never matched from saved preferences; the Quick Add asks instead.
      if (this.isKidsProduct(product)) return null

      // Letter-sized bottoms match on the Bottoms size only, never the top size.
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      if (bottomSizeOptionIndex >= 0)
        return this.getVariantForSelectedBottomSize(product, bottomSizeOptionIndex)

      // 1. Try top size
      if (this.selectedTopSize) {
        for (const variant of variants) {
          if (!variant.available) continue
          if (this.variantMatchesSize(variant, this.selectedTopSize)) return variant
        }
      }

      // 2+3. Try bottom size — single-option: waist only; two-option: waist + length
      for (const variant of variants) {
        if (!variant.available) continue
        const hasTwoOptions =
          variant.option2 && this.normalizeSize(variant.option2) !== 'DEFAULTTITLE'
        if (!hasTwoOptions) {
          if (
            this.selectedBottomWaist &&
            this.variantMatchesWaist(variant, this.selectedBottomWaist)
          )
            return variant
        } else {
          if (
            this.selectedBottomWaist &&
            this.selectedBottomLength &&
            this.variantMatchesBottomSize(
              variant,
              this.selectedBottomWaist,
              this.selectedBottomLength
            )
          )
            return variant
        }
      }

      return null
    }

    // Card JSON carries `isKids`; GraphQL-shaped products carry raw `tags`.
    isKidsProduct(product) {
      if (typeof product?.isKids === 'boolean') return product.isKids
      const tags = Array.isArray(product?.tags) ? product.tags : []
      return tags.some((tag) => ['dep_kids', 'dep_boys', 'dep_girls'].includes(tag))
    }

    // No size can be picked for the shopper (kids, or a set with one size saved), but
    // the product is in stock — so the card stays usable and opens the Quick Add.
    shouldPickSizeInQuickAdd(product) {
      if (this.isKidsProduct(product)) return this.isProductAvailable(product)
      return this.hasPartialSizeMatch(product)
    }

    // Card JSON carries `isBottomsTagged`; GraphQL-shaped products carry raw `tags`.
    getBottomSizeOptionIndex(product) {
      const { getBottomSizeOptionIndex, hasBottomsTag } = window.TcSizePreferences
      const isBottomsTagged = product?.isBottomsTagged ?? hasBottomsTag(product?.tags)
      return getBottomSizeOptionIndex(product?.optionNames, isBottomsTagged)
    }

    // Letter-sized bottoms only count toward a top size through their own Tops/Size
    // option (sets) — a bottoms "M" must not keep the modal's top "M" visible.
    doesProductHaveTopSizeStock(product, size) {
      const variants = product?.variants || []
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      if (bottomSizeOptionIndex < 0)
        return variants.some((v) => v.available && this.variantMatchesSize(v, size))

      const { getTopSizeOptionIndex, doesVariantMatchTopSizeOption } = window.TcSizePreferences
      const topSizeOptionIndex = getTopSizeOptionIndex(product.optionNames, bottomSizeOptionIndex)
      if (topSizeOptionIndex < 0) return false
      return variants.some(
        (v) => v.available && doesVariantMatchTopSizeOption(v, topSizeOptionIndex, size)
      )
    }

    // On a Tops + Bottoms set both sizes must be picked; with only one, there's no full
    // variant (see hasPartialSizeMatch — the Quick Add opens with just that one selected).
    getVariantForSelectedBottomSize(product, bottomSizeOptionIndex) {
      if (!this.selectedBottomSize) return null

      const { getTopSizeOptionIndex, doesVariantMatchBottomSize, doesVariantMatchTopSizeOption } =
        window.TcSizePreferences
      const topSizeOptionIndex = getTopSizeOptionIndex(product.optionNames, bottomSizeOptionIndex)
      const isSet = topSizeOptionIndex >= 0
      if (isSet && !this.selectedTopSize) return null

      return (
        product.variants.find(
          (variant) =>
            variant.available &&
            doesVariantMatchBottomSize(variant, bottomSizeOptionIndex, this.selectedBottomSize) &&
            (!isSet ||
              doesVariantMatchTopSizeOption(variant, topSizeOptionIndex, this.selectedTopSize))
        ) || null
      )
    }

    // Tops option index on a Tops + Bottoms set, or -1 for anything else.
    getSetTopSizeOptionIndex(product) {
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      if (bottomSizeOptionIndex < 0) return -1
      return window.TcSizePreferences.getTopSizeOptionIndex(product.optionNames, bottomSizeOptionIndex)
    }

    // A set where exactly one of the top / Bottoms sizes is picked, and an in-stock
    // variant has that one size. Mirrors the PDP's partial preselect.
    hasPartialSizeMatch(product) {
      const topSizeOptionIndex = this.getSetTopSizeOptionIndex(product)
      if (topSizeOptionIndex < 0) return false
      if (!!this.selectedTopSize === !!this.selectedBottomSize) return false

      const { doesVariantMatchBottomSize, doesVariantMatchTopSizeOption } = window.TcSizePreferences
      const bottomSizeOptionIndex = this.getBottomSizeOptionIndex(product)
      return (product.variants || []).some(
        (variant) =>
          variant.available &&
          (this.selectedTopSize
            ? doesVariantMatchTopSizeOption(variant, topSizeOptionIndex, this.selectedTopSize)
            : doesVariantMatchBottomSize(variant, bottomSizeOptionIndex, this.selectedBottomSize))
      )
    }

    variantMatchesWaist(variant, waist) {
      const normWaist = this.normalizeSize(waist)
      const opts = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .map((o) => this.normalizeSize(o))
      return opts.some((o) => o === normWaist || o === `W${normWaist}`)
    }

    variantMatchesBottomSize(variant, waist, length) {
      const normWaist = this.normalizeSize(waist)
      const normLength = this.normalizeSize(length)
      const lengthNum = normLength.replace(/L$/, '')

      const opts = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .map((o) => this.normalizeSize(o))

      const hasWaist = opts.some((o) => o === normWaist || o === `W${normWaist}`)
      const hasLength = opts.some(
        (o) => o === normLength || o === lengthNum || o === `${lengthNum}L`
      )
      if (hasWaist && hasLength) return true

      const title = this.normalizeSize(variant.title || '')
      const parts = title.split(/[X\/,\-\s]+/)
      if (parts.length >= 2) {
        const tWaist = parts[0].replace(/^W/, '')
        const tLength = parts[1].replace(/^L/, '').replace(/L$/, '')
        if (tWaist === normWaist && (tLength === lengthNum || parts[1] === normLength))
          return true
      }

      return false
    }

    variantMatchesSize(variant, size) {
      const aliases = this.getSizeAliases(size).map((alias) => this.normalizeSize(alias))
      const values = [variant.option1, variant.option2, variant.option3, variant.title].filter(
        Boolean
      )

      for (const value of values) {
        const normalizedValue = this.normalizeSize(value)
        if (aliases.includes(normalizedValue)) return true

        const valueParts = normalizedValue
          .split(/[\/,-]/)
          .map((part) => part.trim())
          .filter(Boolean)

        for (const part of valueParts) {
          if (aliases.includes(part)) return true
        }
      }

      return false
    }

    getSizeAliases(size) {
      return window.TcSizePreferences.getTopSizeAliases(size)
    }

    normalizeSize(value) {
      return window.TcSizePreferences.normalizeSize(value)
    }

    // The Bottoms button's own text (e.g. "M" for MD where the picker uses short labels).
    getBottomSizeDisplayLabel(size) {
      const button = this.bottomSizeButtons?.find((btn) => btn.dataset.size === size)
      return button?.textContent.trim() || size
    }

    getSelectedSizeSummary() {
      const parts = []

      if (this.selectedTopSize) {
        parts.push(this.selectedTopSize)
      }

      if (this.selectedBottomSize) {
        parts.push(`Bottoms ${this.getBottomSizeDisplayLabel(this.selectedBottomSize)}`)
      }

      if (this.selectedBottomWaist && this.selectedBottomLength) {
        parts.push(`W${this.selectedBottomWaist} x ${this.selectedBottomLength}`)
      }

      return parts.join(' · ') || 'Not set'
    }

    updateSelectedSizeLabel() {
      if (!this.selectedSizeLabel) return

      if (this.selectedSizeLabel.dataset.sizeSummaryStyle === 'labeled') {
        this.renderLabeledSizeSummary()
        return
      }
      this.selectedSizeLabel.textContent = this.getSelectedSizeSummary()
    }

    // "30x28L", or whichever half is picked.
    getWaistLengthSelection() {
      return [this.selectedBottomWaist, this.selectedBottomLength].filter(Boolean).join('x')
    }

    // [type, selections] per group with at least one picked size.
    getLabeledSizeSelections() {
      return [
        ['Tops', [this.selectedTopSize, this.selectedTopLength]],
        [
          'Bottoms',
          [
            this.selectedBottomSize && this.getBottomSizeDisplayLabel(this.selectedBottomSize),
            this.getWaistLengthSelection(),
          ],
        ],
      ]
        .map(([type, selections]) => [type, selections.filter(Boolean)])
        .filter(([, selections]) => selections.length)
    }

    createSizeSummarySpan(className, text) {
      const span = document.createElement('span')
      span.className = className
      span.textContent = text
      return span
    }

    // One group: "Tops: S · Regular".
    createSizeSummaryGroup(type, selections) {
      const group = this.createSizeSummarySpan('size-bar__group', '')
      group.append(this.createSizeSummarySpan('size-bar__type', `${type}: `))
      selections.forEach((selection, index) => {
        if (index > 0) group.append(this.createSizeSummarySpan('size-bar__separator', ' · '))
        group.append(this.createSizeSummarySpan('size-bar__selection', selection))
      })
      return group
    }

    // "Tops: S · Regular  Bottoms: L · 30x28L" — type in the label color, selections in the value color.
    renderLabeledSizeSummary() {
      const groups = this.getLabeledSizeSelections()
      if (!groups.length) {
        this.selectedSizeLabel.textContent = 'Not set'
        return
      }

      this.selectedSizeLabel.replaceChildren(
        ...groups.map(([type, selections]) => this.createSizeSummaryGroup(type, selections))
      )
    }

    updateBottomSizePrompt() {
      if (!this.bottomSizePrompt) return

      this.bottomSizePrompt.hidden = !!(
        this.selectedBottomWaist && this.selectedBottomLength
      )
    }

    updateBrowse() {
      this.updateSelectedSizeLabel()
      this.updateBottomSizePrompt()
      // A/B test concluded: this.renderAutomaticBundles()
      this.updateSinglePricing()

      const isDealsActive = this.activeTab === 'deals'
      let visibleSingleCount = 0

      if (this.dealsView) {
        this.dealsView.hidden = !isDealsActive
      }

      // Two passes: availability first, then the Last Call Clearance length is resolved from it,
      // then visibility is applied.
      const singleStates = (this.singleCards || []).map((card) => ({
        card,
        hasProducts: this.parseProducts(card, '[data-clearance-v3-single-products]').length > 0,
        isAvailable: this.hasSelectableItems(
          card,
          '[data-clearance-v3-single-products]',
          false
        ),
      }))
      const effectiveSingleLength = this.resolveSingleLengthFilter(singleStates)

      for (const state of singleStates) {
        const { card, hasProducts, isAvailable } = state
        const matchesLength = this.matchesTopLengthFilter(card, effectiveSingleLength)
        const isVisible = isDealsActive && hasProducts && matchesLength

        card.hidden = !isVisible
        card.dataset.sizeAvailable = isAvailable ? 'true' : 'false'

        const sizeElement = card.querySelector('[data-clearance-v3-card-size]')
        if (sizeElement) sizeElement.textContent = this.getCardSizeLabel(card)

        const button = card.querySelector('[data-clearance-v3-add-single]')
        if (button) {
          const hasTransientState =
            button.classList.contains('added') || button.getAttribute('aria-busy') === 'true'
          if (!hasTransientState) {
            button.disabled = !isAvailable
            this.updateButtonLabel(
              button,
              isAvailable
                ? button.dataset.defaultLabel || 'Add To Cart'
                : `Unavailable in ${this.getCardSizeLabel(card)}`
            )
          }
        }

        if (isVisible && isAvailable) visibleSingleCount += 1
        if (isDealsActive) this.setSingleCardParked(card, !(isVisible && isAvailable))
      }

      // A size change that reveals more cards lets auto-load pick up again.
      if (visibleSingleCount > this.visibleSingleCount) this.singlesAutoLoadPaused = false

      // Counts what is loaded and passes the filter, rather than promising the whole
      // collection. Hidden at zero: the empty state covers that.
      if (this.singlesCountElement) {
        const showSinglesCount = visibleSingleCount > 0
        this.singlesCountElement.hidden = !showSinglesCount
        if (showSinglesCount) {
          this.singlesCountElement.textContent = `${visibleSingleCount} ${
            visibleSingleCount === 1 ? 'item' : 'items'
          }`
        }
      }

      // Only truly empty once there is nothing left to load — with pagination a size
      // filter can wipe out the loaded pages while later ones still hold matches.
      if (this.emptyElement) {
        this.emptyElement.hidden =
          !isDealsActive || visibleSingleCount > 0 || Number(this.singlesNextPage || 0) > 0
      }

      this.visibleSingleCount = visibleSingleCount
      this.syncSinglesPaginationState()
    }

    // Cards the shopper can't see (wrong size, or filtered out) are swapped for a comment
    // placeholder, so a long session doesn't keep hundreds of hidden cards in the page.
    // They go back in the same spot as soon as a filter change makes them visible.
    setSingleCardParked(card, shouldPark) {
      this.parkedSingleCards ??= new Map()
      const placeholder = this.parkedSingleCards.get(card)

      if (shouldPark && !placeholder && card.isConnected) {
        const marker = document.createComment('hidden sale card')
        card.replaceWith(marker)
        this.parkedSingleCards.set(card, marker)
        return
      }

      if (!shouldPark && placeholder) {
        placeholder.replaceWith(card)
        this.parkedSingleCards.delete(card)
      }
    }

    getParkedSingleCards() {
      return Array.from(this.parkedSingleCards?.keys() || [])
    }

    updateSinglePricing() {
      for (const card of this.singleCards || []) {
        // Tag mode prices from the discount tags alone, so a card can be
        // clearance-priced without the Clearance tag. Quantity mode still needs
        // the Clearance tag, because its tiers are not tag-driven.
        const isClearancePriced = isClearanceTagModeSafe()
          ? card.dataset.clearanceSelectTagged === 'true' ||
            card.dataset.clearanceBaseTagged === 'true'
          : card.dataset.clearanceTagged === 'true'
        const selectedItems = this.getSelectableItems(
          card,
          '[data-clearance-v3-single-products]',
          false
        )
        const itemCount = selectedItems.length || 1
        const basePrice = selectedItems.length
          ? this.getItemPrice(selectedItems[0])
          : Number(card.dataset.basePrice || 0)
        if (!basePrice) continue

        const priceElement = card.querySelector('[data-clearance-v3-card-price]')

        if (isClearancePriced) {
          const predictedDiscount = this.getDiscountForCount(this.clearanceCount + itemCount, card)
          const discountedPrice = this.getDiscountedPrice(basePrice, predictedDiscount)
          const wasElement = card.querySelector('[data-clearance-v3-clearance-was]')
          const discElement = card.querySelector('[data-clearance-v3-clearance-disc]')

          // The clearance rate comes off the product's price, but the shopper is
          // saving against the compare-at price — so that is what gets struck
          // through, and the badge is the total saving between the two prices
          // actually on the card rather than the clearance rate alone.
          const compareAtPrice = selectedItems.length
            ? this.getItemCompareAtPrice(selectedItems[0])
            : Number(card.dataset.clearanceCompareAtPrice || 0)
          const wasPrice = Math.max(compareAtPrice || 0, basePrice)
          const totalDiscount =
            wasPrice > discountedPrice
              ? Math.round(((wasPrice - discountedPrice) / wasPrice) * 100)
              : predictedDiscount

          card.dataset.discountActive = predictedDiscount > 0 ? 'true' : 'false'
          if (priceElement) priceElement.textContent = this.formatMoney(discountedPrice)
          if (wasElement) {
            wasElement.hidden = predictedDiscount <= 0
            wasElement.textContent = this.formatMoney(wasPrice)
          }
          if (discElement) {
            discElement.hidden = predictedDiscount <= 0
            discElement.textContent = `${totalDiscount}% OFF`
          }
        } else {
          card.dataset.discountActive = 'false'
          if (priceElement) priceElement.textContent = this.formatMoney(basePrice)
        }
      }
    }

    getItemPrice(item) {
      return Number(item?.variant?.price || item?.product?.price || 0)
    }

    // The item's compare-at price, falling back to the product's and then to 0
    // for a product that carries no compare-at price at all.
    getItemCompareAtPrice(item) {
      return Number(item?.variant?.compareAtPrice || item?.product?.compareAtPrice || 0)
    }

    // Plain-text form of the shared savings line, for surfaces that take text
    // rather than HTML (the toast).
    getTagModeSavingsText() {
      const html = window.TC_CLEARANCE_CONFIG?.savingsMessageHtml || ''
      const el = document.createElement('div')
      el.innerHTML = html
      return el.textContent.trim()
    }

    // The section's "Show add to cart message" setting; shown unless explicitly off.
    shouldShowAddToast() {
      return this.dataset.showAddToast !== 'false'
    }

    getToastSubtitle() {
      if (isClearanceTagModeSafe()) return this.getTagModeSavingsText()
      const nextTier = this.getNextTier(this.clearanceCount)
      if (!nextTier) return 'All savings unlocked'
      const needed = nextTier.quantity - this.clearanceCount
      const itemLabel = needed === 1 ? 'item' : 'items'
      return `${needed} more ${itemLabel} for ${nextTier.percentage}% OFF`
    }

    getToastContainer() {
      let wrap = document.getElementById('c3-toast-wrap')
      if (!wrap) {
        wrap = document.createElement('div')
        wrap.id = 'c3-toast-wrap'
        wrap.className = 'c3-toast-wrap'
        document.body.appendChild(wrap)
      }

      let status = wrap.querySelector('[data-clearance-v3-toast-status]')
      if (!status) {
        status = this.createSrOnlySpan('')
        status.dataset.clearanceV3ToastStatus = ''
        status.setAttribute('role', 'status')
        status.setAttribute('aria-live', 'polite')
        status.setAttribute('aria-atomic', 'true')
        wrap.prepend(status)
      }

      return wrap
    }

    showToast(title, subtitle, isTierUp, isMaxTier) {
      const wrap = this.getToastContainer()
      const status = wrap.querySelector('[data-clearance-v3-toast-status]')
      const toast = document.createElement('button')
      toast.type = 'button'
      toast.className = `toast${isTierUp ? ' tier-up' : ''}${isMaxTier ? ' max-tier' : ''}`
      toast.setAttribute('aria-label', `${title}. ${subtitle}. Open cart.`)

      const icon = document.createElement('span')
      icon.className = 'toast-icon'
      icon.setAttribute('aria-hidden', 'true')
      icon.textContent = isTierUp ? '🎉' : '✓'

      const content = document.createElement('span')
      const titleElement = document.createElement('span')
      titleElement.className = 'toast-title'
      titleElement.textContent = title
      const subtitleElement = document.createElement('span')
      subtitleElement.className = 'toast-sub'
      subtitleElement.textContent = subtitle
      content.append(titleElement, subtitleElement)
      toast.append(icon, content)

      toast.addEventListener('click', () => this.openCartDrawer())
      wrap.appendChild(toast)
      if (status) {
        status.textContent = ''
        requestAnimationFrame(() => {
          status.textContent = `${title}. ${subtitle}`
        })
      }
      requestAnimationFrame(() => requestAnimationFrame(() => toast.classList.add('show')))

      const removeToast = () => {
        if (document.activeElement === toast || toast.matches(':hover')) {
          window.setTimeout(removeToast, 1000)
          return
        }
        toast.classList.remove('show')
        window.setTimeout(() => toast.remove(), 350)
      }
      window.setTimeout(removeToast, isTierUp ? 5000 : 4000)
    }

    // Deal rows aren't necessarily clearance; only singles and clearance-tagged variants count toward tiers.
    isClearanceAdd(variantId, source) {
      if (source === 'single') return true
      return this.clearanceVariantIds?.has(Number(variantId)) === true
    }

    hasClearanceItem(items, source) {
      const isClearanceItem = (item) => this.isClearanceAdd(item.variant.id, source)
      return items.some(isClearanceItem)
    }

    getClearanceProperties(source) {
      return {
        _clearance: 'true',
        _clearance_size: this.selectedTopSize,
        _clearance_source: source,
      }
    }

    async addItemsToCart(items, button, card, source) {
      if (!items.length) {
        this.setTemporaryButtonLabel(button, 'Unavailable')
        return
      }

      this.setButtonLoading(button)
      document.dispatchEvent(
        new CustomEvent('clearanceatcstart', {
          bubbles: true,
          detail: { quantity: items.length },
        })
      )

      // Non-clearance adds open the drawer; do it now on a skeleton instead of after the response
      const cartDrawer = document.querySelector('cart-drawer')
      const isOpeningCartEarly =
        !this.hasClearanceItem(items, source) && typeof cartDrawer?.openWithSkeleton === 'function'
      if (isOpeningCartEarly) cartDrawer.openWithSkeleton()

      try {
        const cartItems = []
        for (const item of items) {
          const id = item.variant.id
          const existingItem = cartItems.find((cartItem) => cartItem.id === id)
          if (existingItem) {
            existingItem.quantity += 1
            continue
          }

          const cartItem = { id, quantity: 1 }
          if (this.isClearanceAdd(id, source)) cartItem.properties = this.getClearanceProperties(source)
          cartItems.push(cartItem)
        }

        const response = await fetch(`${this.routesRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: cartItems,
          }),
        })
        const data = await response.json()

        if (!response.ok || data.status) {
          throw new Error(data.message || 'Unable to add clearance items')
        }

        card?.classList.add('in-cart')
        this.setButtonAdded(button)
        // Non-clearance adds open the cart drawer (site default) instead of showing the toast.
        const shouldOpenCart = !this.hasClearanceItem(items, source)
        if (!shouldOpenCart)
          this._pendingAddTitle = items[0]?.product?.title || items[0]?.product?.color || 'Added!'
        const variantId = items[0]?.variant?.id || null

        document.dispatchEvent(
          new CustomEvent('clearanceatcsuccess', {
            bubbles: true,
            detail: {
              quantity: items.length,
              variantId,
            },
          })
        )
        document.dispatchEvent(
          new CustomEvent('cartaddsuccess', {
            bubbles: true,
            detail: {
              openCart: shouldOpenCart,
              refreshCart: true,
              variantId,
            },
          })
        )

        await this.refreshCartSummary()
      } catch (error) {
        console.error('Sale Page add failed', error)
        if (isOpeningCartEarly) cartDrawer.hideLineItemSkeleton()
        this.setTemporaryButtonLabel(button, 'Unable to add')
      } finally {
        window.setTimeout(() => {
          this.restoreButton(button)
        }, 1400)
      }
    }

    setButtonLoading(button) {
      if (!button) return

      button.disabled = true
      button.setAttribute('aria-busy', 'true')
      this.updateButtonLabel(button, 'Adding...')
    }

    updateButtonLabel(button, label) {
      if (!button) return

      const normalizedLabel = String(label || '').trim()
      const productTitle = String(button.dataset.productTitle || '').trim()
      const accessibleName = [normalizedLabel, productTitle].filter(Boolean).join(' ')
      const inCartStatus = button.dataset.inCart === 'true' ? ' Currently in cart.' : ''

      // Buttons with fire ring layers keep their text in [data-button-label], so the layers
      // survive the label change.
      const labelElement = button.querySelector('[data-button-label]') || button
      labelElement.textContent = normalizedLabel
      button.setAttribute('aria-label', `${accessibleName}.${inCartStatus}`.trim())
    }

    createSrOnlySpan(text) {
      const span = document.createElement('span')
      span.className = 'visually-hidden'
      span.textContent = text
      return span
    }

    setButtonAdded(button) {
      if (!button) return

      button.classList.add('added')
      button.removeAttribute('aria-busy')
      this.updateButtonLabel(button, button.dataset.addedLabel || 'Added')
    }

    setTemporaryButtonLabel(button, label) {
      if (!button) return

      button.disabled = true
      button.removeAttribute('aria-busy')
      this.updateButtonLabel(button, label)

      window.setTimeout(() => {
        this.restoreButton(button)
      }, 1400)
    }

    restoreButton(button) {
      if (!button) return

      button.disabled = false
      button.removeAttribute('aria-busy')
      button.classList.remove('added')
      this.updateButtonLabel(button, button.dataset.defaultLabel || 'Add To Cart')
    }

    async refreshCartSummary() {
      const summary = await this.fetchClearanceSummary()
      this.updateTierStrip(summary)
      this.syncCardCartState(summary.variantIds, summary.variantQuantities)
      this.updateBrowse()
    }

    async fetchClearanceSummary() {
      try {
        const response = await fetch(`${this.routesRoot()}cart.js`, {
          headers: { Accept: 'application/json' },
        })
        const cart = await response.json()
        let count = 0
        let subtotal = 0
        let originalSubtotal = 0
        const variantIds = new Set()
        const variantQuantities = new Map()

        const singlesProductIds = new Set(
          (this.parseAllProducts()?.singles || []).map((p) => Number(p.productId))
        )

        for (const item of cart.items || []) {
          const isClearanceTaggedVariant =
            this.clearanceVariantIds?.size > 0 &&
            this.clearanceVariantIds.has(Number(item.variant_id || item.id || 0))
          const isClearanceFlowItem = item.properties?._clearance === 'true'
          if (!isClearanceTaggedVariant && !isClearanceFlowItem) continue

          count += Number(item.quantity || 0)
          subtotal += Number(item.final_line_price || item.line_price || 0)
          originalSubtotal += Number(item.original_line_price || item.line_price || 0)

          const variantId = Number(item.variant_id || item.id || 0)
          if (variantId) {
            variantIds.add(variantId)
            variantQuantities.set(
              variantId,
              (variantQuantities.get(variantId) || 0) + Number(item.quantity || 0)
            )
          }
        }

        return { count, subtotal, originalSubtotal, variantIds, variantQuantities }
      } catch (error) {
        return {
          count: this.clearanceCount,
          subtotal: this.clearanceSubtotal,
          variantIds: this.cartVariantIds,
          variantQuantities: this.cartVariantQuantities,
        }
      }
    }

    syncCardCartState(variantIds, variantQuantities = null) {
      this.cartVariantIds = variantIds instanceof Set ? variantIds : new Set()
      this.cartVariantQuantities =
        variantQuantities instanceof Map ? variantQuantities : new Map()

      for (const card of this.singleCards || []) {
        const items = this.getSelectableItems(card, '[data-clearance-v3-single-products]', false)
        const isInCart = items.some((item) => this.cartVariantIds.has(Number(item.variant.id)))
        card.classList.toggle('in-cart', isInCart)
        this.syncCardInCartText(card, '[data-clearance-v3-add-single]', isInCart)
      }
    }

    syncCardInCartText(card, buttonSelector, isInCart) {
      const button = card.querySelector(buttonSelector)
      if (!button) return

      button.dataset.inCart = isInCart ? 'true' : 'false'
      this.updateButtonLabel(button, button.textContent || button.dataset.defaultLabel || 'Add To Cart')
    }

    handleQuickAddSuccess(event) {
      if (event.detail?.source !== 'clearance-v3-quick-add') return

      const productId = String(event.detail.productId || '')
      if (productId) {
        for (const card of this.singleCards || []) {
          if (String(card.dataset.productId) === productId) card.classList.add('in-cart')
        }
      }

      this._pendingAddTitle = event.detail.productTitle || 'Added!'
      this.refreshCartSummary()
    }

    updateTierStrip(summary) {
      this.clearanceCount = Number(summary.count || 0)
      this.clearanceSubtotal = Number(summary.subtotal || 0)

      // The tier-unlock toast ("X% Off Unlocked!") is intentionally gone; only the
      // add-to-cart confirmation still surfaces.
      if (this._pendingAddTitle) {
        const title = this._pendingAddTitle
        this._pendingAddTitle = null
        if (this.shouldShowAddToast()) this.showToast(title, this.getToastSubtitle(), false)
      }

    }

    // Overall progress toward the top tier, mirroring the clearance-banner
    // sticky bar fill.
    getCurrentTier(count) {
      let currentTier = null
      for (const tier of this.tiers) {
        if (count >= tier.quantity) currentTier = tier
      }
      return currentTier
    }

    getNextTier(count) {
      return this.tiers.find((tier) => count < tier.quantity) || null
    }

    // `card` is only consulted in tag mode, where the discount is fixed by the
    // product's own tags and `count` is irrelevant.
    getDiscountForCount(count, card) {
      if (isClearanceTagModeSafe()) {
        return resolveClearanceDiscount({
          hasSelectTag: card?.dataset.clearanceSelectTagged === 'true',
          hasBaseTag: card?.dataset.clearanceBaseTagged === 'true',
        })
      }
      const tier = this.getCurrentTier(count)
      return tier ? tier.percentage : this.baseDiscount
    }

    // Per-item discount for packs, whose items can be tagged differently from
    // each other in tag mode.
    getDiscountForItem(item, count) {
      if (!isClearanceTagModeSafe()) return this.getDiscountForCount(count)
      return resolveClearanceDiscount({
        hasSelectTag: !!item?.product?.selectTagged,
        hasBaseTag: !!item?.product?.baseTagged,
      })
    }

    // Mirrors Shopify's per-unit "each" allocation: the discount is truncated
    // (floored) to the cent, then subtracted. Keeps card prices matching the cart.
    getDiscountedPrice(price, percentage) {
      const discountAmount = Math.floor((price * percentage) / 100)
      return price - discountAmount
    }

    // Recovers the active market's currency format from data-money-probe — a price the
    // section rendered through Liquid's own `money` filter. Because the probe's cents
    // amount is also known, the digit count alone says whether the market shows a minor
    // unit, and the remaining characters give the symbol placement and separators.
    // Deliberately not window.Shopify.formatMoney: that is pinned to the USD
    // `${{amount}}` format in global.js, so it prints "$" on every market. Memoized —
    // the probe cannot change without a re-render.
    moneyParts() {
      if (this.parsedMoneyParts) return this.parsedMoneyParts

      const probe = this.dataset.moneyProbe || ''
      const probeCents = Number(this.dataset.moneyProbeCents)
      const firstDigit = probe.search(/\d/)
      const lastDigit = probe.length - 1 - probe.split('').reverse().join('').search(/\d/)

      // USD default, matching the previous behaviour if the probe is missing or unparseable.
      this.parsedMoneyParts = { prefix: '$', suffix: '', thousands: ',', decimal: '.', precision: 2 }

      if (!Number.isFinite(probeCents) || probeCents <= 0 || firstDigit < 0 || lastDigit < firstDigit) {
        return this.parsedMoneyParts
      }

      const core = probe.slice(firstDigit, lastDigit + 1)
      const precision = core.replace(/\D/g, '').length - String(Math.round(probeCents / 100)).length
      if (precision !== 0 && precision !== 2) return this.parsedMoneyParts

      // With a minor unit, the last two digits are always preceded by the decimal
      // separator, which leaves the grouping separator as the last non-digit before it.
      const decimal = precision === 2 ? core.charAt(core.length - 3) : ''
      if (precision === 2 && (!decimal || /\d/.test(decimal))) return this.parsedMoneyParts

      const whole = precision === 2 ? core.slice(0, core.length - 3) : core
      const separators = whole.replace(/\d/g, '')

      this.parsedMoneyParts = {
        prefix: probe.slice(0, firstDigit),
        suffix: probe.slice(lastDigit + 1),
        thousands: separators ? separators.charAt(separators.length - 1) : '',
        decimal: decimal,
        precision: precision,
      }

      return this.parsedMoneyParts
    }

    // Mirrors the Liquid `{% render 'money-prefix' %}{{ value | money }}` output used by
    // the server-rendered cards, so client-side repricing keeps the market's currency.
    // A zero minor unit is dropped (as before) by omitting it rather than by matching a
    // trailing ".00" — formats such as `${{amount}} CAD` and `{{amount}} €` end in a suffix.
    formatMoney(amountInCents) {
      const amount = Number(amountInCents)
      if (!Number.isFinite(amount)) return ''

      const parts = this.moneyParts()
      const [whole, minor] = (amount / 100).toFixed(parts.precision).split('.')
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${parts.thousands}`)
      const value = minor && minor !== '00' ? grouped + parts.decimal + minor : grouped

      return (this.dataset.moneyPrefix || '') + parts.prefix + value + parts.suffix
    }

    routesRoot() {
      return window.Shopify?.routes?.root || '/'
    }
  }

  customElements.define('sale-page-flow', SalePageFlow)
}
